import { collapseMessageTrace, collapseMessageTraceInStream, recipientSummary, recipientStatusSummary } from './messageTraceCollapse.js';
import { mergeNonInteractiveIpList, nonInteractiveExcludedIpCount, nonInteractiveIpAggregates, nonInteractiveAppLabel } from './nonInteractiveRollup.js';
import { underlyingEventCount, underlyingEventTotal, rowAppLabel } from './allEventsRollups.js';
import { buildAttackerContext, attackerBasis } from './attackerAttribution.js';
import {
  flaggedPhishMessageIndex, collectReleaseEvidence, maliciousActivityBounds,
  selectRelatedMessages, relatedMessageSilverId, relatedPhishingRows, RELATED_SCOPE_PAD,
  relatedIsSameMessage, relatedRecipientClicks,
} from './quarantineRelease.js';
/**
 * storyGenerator.js — Generate investigation reports as self-contained HTML pages.
 *
 * Two report types:
 *   - Client Report: Executive brief + detailed findings (for office managers/executives)
 *   - Technical Report: Full technical tables up front (for security analysts)
 *
 * Both open in a new browser tab with embedded CSS and a "Print / Export PDF" button.
 */

import {
  combineAndTagEvents,
  buildIpLookup,
  classifyIP,
  getExchangeCategory,
  getDirAuditDetail,
  getDisplayOperation,
  getSecurityAlertSummary,
  deduplicateAlerts,
  FILE_OPERATIONS,
  LIST_OPERATIONS,
  parseBrowser,
  maskSubject,
  maskFileName,
  parseFolders,
  getMailAccessType,
} from './eventUtils';
import { TIMEZONE_OPTIONS } from '../components/TimelineView';
import { buildExposures } from './attackerAccess.js';
import { buildExecReportFactPack, attemptWindowSpan } from './execReportFactPack.js';

// ── HTML Escaping ────────────────────────────────────────────────────────────

function esc(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ── Timestamp Formatting ─────────────────────────────────────────────────────

function getTzIana(tzId) {
  const tz = TIMEZONE_OPTIONS.find(t => t.id === tzId);
  return tz ? tz.iana : 'UTC';
}

function getTzAbbr(iana, date) {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', { timeZone: iana, timeZoneName: 'short' });
    const parts = fmt.formatToParts(date);
    return parts.find(p => p.type === 'timeZoneName')?.value || '';
  } catch { return ''; }
}

function formatTs(ts, tzId) {
  if (!ts) return '\u2014';
  try {
    const d = new Date(ts);
    const iana = getTzIana(tzId);
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: iana,
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
    return fmt.format(d) + ' ' + getTzAbbr(iana, d);
  } catch { return String(ts); }
}

function formatTsPlain(ts, tzId) {
  if (!ts) return '\u2014';
  try {
    const d = new Date(ts);
    const iana = getTzIana(tzId);
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: iana,
      year: 'numeric', month: 'long', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
      hour12: true,
    });
    return fmt.format(d) + ' ' + getTzAbbr(iana, d);
  } catch { return String(ts); }
}

// \ud83d\udd34 A BARE `YYYY-MM-DD` IS A CALENDAR DAY, NOT AN INSTANT. `new Date('2026-10-22')` is UTC
// midnight, which in any US zone is the evening BEFORE, so every report's "Reviewed logs"
// line printed both ends a day early (IR-20261026-jturbyville-001: "October 21 to October 25"
// for a 10-22..10-26 case). A calendar day is formatted as itself, with no zone conversion.
export function formatDateOnly(ts, tzId) {
  if (!ts) return '\u2014';
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ts).trim());
  if (day) {
    const d = new Date(Date.UTC(+day[1], +day[2] - 1, +day[3], 12));
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC', year: 'numeric', month: 'long', day: 'numeric',
    }).format(d);
  }
  try {
    const d = new Date(ts);
    const iana = getTzIana(tzId);
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: iana,
      year: 'numeric', month: 'long', day: 'numeric',
    });
    return fmt.format(d);
  } catch { return String(ts); }
}

function formatDuration(startIso, endIso) {
  if (!startIso || !endIso) return 'unknown duration';
  const ms = new Date(endIso) - new Date(startIso);
  if (ms < 0) return 'unknown duration';
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  const parts = [];
  parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  parts.push(`${mins} minute${mins !== 1 ? 's' : ''}`);
  parts.push(`${secs} second${secs !== 1 ? 's' : ''}`);
  return parts.join(', ');
}

// The active-period duration, rounded exactly as the Brief's `durationDisplay`
// (execReportFactPack formatDurationText: "~33 min", "~4.5 hr", "~1.0 days") and written out,
// so both documents state the same figure (v1.203.0). The Client Report printed
// "approximately 0 days, 0 hours, 0 minutes, 40 seconds" where the Brief said "~1 min".
function formatDurationApprox(startIso, endIso) {
  if (!startIso || !endIso) return '';
  const ms = new Date(endIso) - new Date(startIso);
  if (!(ms >= 0)) return '';
  const mins = Math.round(ms / 60000);
  if (mins < 1) return 'under a minute';
  if (mins < 60) return `about ${mins} minute${mins !== 1 ? 's' : ''}`;
  const hrs = mins / 60;
  if (hrs < 24) return `about ${hrs.toFixed(hrs < 10 ? 1 : 0)} hours`;
  return `about ${(hrs / 24).toFixed(1)} days`;
}

// A compact duration for episode narratives: drops zero leading units and trailing seconds
// once the span is at least a minute, so "0 days, 2 hours, 25 minutes, 20 seconds" reads as
// "2 hours 25 minutes". Used only by the episode wording (v1.183.0).
function formatDurationShort(startIso, endIso) {
  if (!startIso || !endIso) return '';
  const ms = new Date(endIso) - new Date(startIso);
  if (ms < 0) return '';
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  const u = (n, w) => `${n} ${w}${n !== 1 ? 's' : ''}`;
  if (days > 0) return hours > 0 ? `${u(days, 'day')} ${u(hours, 'hour')}` : u(days, 'day');
  if (hours > 0) return mins > 0 ? `${u(hours, 'hour')} ${u(mins, 'minute')}` : u(hours, 'hour');
  if (mins > 0) return u(mins, 'minute');
  return u(secs, 'second');
}

// ── IP Classification for HTML output ────────────────────────────────────────

function ipColorClass(classification) {
  switch (classification) {
    case 'malicious': return 'ip-malicious';
    case 'suspect': return 'ip-suspect';
    case 'safe': return 'ip-safe';
    case 'ms-infra': return 'ip-ms-infra';
    default: return 'ip-default';
  }
}

function ipStatusLabel(classification) {
  switch (classification) {
    case 'malicious': return 'Malicious';
    case 'suspect': return 'Suspect';
    case 'safe': return 'Safe';
    case 'ms-infra': return 'MS-Infra';
    case 'ms-compute': return 'Azure-VM';
    default: return 'Default';
  }
}

function ipStatusBadge(classification) {
  const label = ipStatusLabel(classification);
  const cls = classification === 'malicious' ? 'badge-red'
    : classification === 'suspect' ? 'badge-amber'
    : classification === 'safe' ? 'badge-green'
    : classification === 'ms-infra' ? 'badge-blue'
    : 'badge-gray';
  return `<span class="badge ${cls}">${esc(label)}</span>`;
}

function riskBadge(score) {
  if (score == null || score === '') return '';
  const n = Number(score);
  if (isNaN(n)) return '';
  const cls = n >= 100 ? 'badge-red' : n >= 30 ? 'badge-amber' : n >= 0 ? 'badge-green' : 'badge-blue';
  return `<span class="badge ${cls}">${n}</span>`;
}

function typeBadge(eventType) {
  const map = {
    'SignIn': ['Sign-In', 'type-signin'],
    'Exchange': ['Exchange', 'type-exchange'],
    'SharePoint': ['SharePoint', 'type-sharepoint'],
    'SharePointList': ['SP List', 'type-splist'],
    'DirectoryAudit': ['Dir Audit', 'type-diraudit'],
    'MessageTrace': ['Msg Trace', 'type-msgtrace'],
    'UrlClick': ['URL Click', 'type-urlclick'],
    'RiskDetection': ['Risk Det.', 'type-riskdet'],
    'RiskyUser': ['Risky User', 'type-riskyuser'],
    'DLP': ['DLP', 'type-dlp'],
    'General': ['General', 'type-general'],
    'AzureActivity': ['Azure', 'type-azure'],
    'AlertEvidence': ['\u26A0 Evidence', 'type-alert-evidence'],
    'SecurityAlert': ['Alert', 'type-alert'],
    'SecurityIncident': ['Incident', 'type-incident'],
    // Same label the timeline uses (TimelineView getTypeBadge) -- a non-interactive
    // sign-in is an existing token redeemed without the user authenticating again.
    'NonInteractiveSignIn': ['Token Use', 'type-tokenuse'],
  };
  const [label, cls] = map[eventType] || [eventType, 'type-default'];
  return `<span class="badge ${cls}">${esc(label)}</span>`;
}

function boolCell(val) {
  if (val === true) return '<span class="text-red-light">Yes</span>';
  return '<span class="text-muted">—</span>';
}

// ── Render IP cell with color ────────────────────────────────────────────────

function renderIP(ip, ctx, event) {
  if (!ip) return '<span class="text-muted">—</span>';
  const cls = classifyIP(ip, ctx.safeIPSet, ctx.ipLookup, ctx.ipClassifications, event);
  const note = ctx.ipComments && ctx.ipComments[ip]
    ? ` <span class="text-muted text-tiny">${esc(ctx.ipComments[ip])}</span>` : '';
  return `<span class="${ipColorClass(cls)}">${esc(ip)}</span>${note}`;
}

function isMsInfraIP(ip, ctx) {
  const info = ctx.ipLookup && ctx.ipLookup[ip];
  if (!info) return false;
  const tier = info.AzureServiceTier || info.azureServiceTier || '';
  return tier === 'MS-Backend' || tier === 'MS-Shared-Infra';
}

function renderIPCell(event, ctx, maxDisplay = 3) {
  const ip = event.IPAddress;
  const allResolved = event.AllResolvedIPs;
  // Multi-IP resolved — filter out MS-Infra, cap display
  if (allResolved && allResolved.includes(',')) {
    const all = allResolved.split(',').map(s => s.trim()).filter(Boolean);
    const realIPs = all.filter(rip => !isMsInfraIP(rip, ctx));
    if (realIPs.length === 0) return '<span class="ip-ms-infra">Microsoft Service</span>';
    const visible = realIPs.slice(0, maxDisplay);
    const rendered = visible.map(rip => renderIP(rip, ctx, event));
    if (realIPs.length > maxDisplay) {
      rendered.push(`<span class="text-muted text-tiny">+${realIPs.length - maxDisplay} more</span>`);
    }
    if (realIPs.length > 1) {
      rendered.push('<span class="text-muted text-tiny">(shared session)</span>');
    }
    return rendered.join('<br>');
  }
  // Single resolved (different from display IP) — skip MS-Infra original
  if (event.ResolvedUserIP && event.ResolvedUserIP !== ip) {
    return renderIP(event.ResolvedUserIP, ctx, event);
  }
  // Original IP is MS-Infra with no resolution
  if (ip && isMsInfraIP(ip, ctx)) {
    return '<span class="ip-ms-infra">Microsoft Service</span>';
  }
  // Inferred IP
  if (!ip && event.InferredIP) {
    const conf = event.InferredIPConfidence;
    const sym = conf === 'high' ? '✓' : conf === 'medium' ? '~' : '?';
    const note = ctx.ipComments && ctx.ipComments[event.InferredIP]
      ? ` <span class="text-tiny">${esc(ctx.ipComments[event.InferredIP])}</span>` : '';
    return `<span class="text-muted"><em>${esc(event.InferredIP)}</em> <span class="text-tiny">${sym}</span>${note}</span>`;
  }
  return renderIP(ip, ctx, event);
}

function renderVPNProxy(flags) {
  if (!flags) return '';
  const parts = [];
  if (flags.isVPN) parts.push('<span class="text-amber-light">VPN</span>');
  if (flags.isProxy) parts.push('<span class="text-red-light">Proxy</span>');
  if (flags.isTOR) parts.push('<span class="text-red-light">TOR</span>');
  if (flags.isCompromised) parts.push('<span class="text-red-light">Compromised</span>');
  if (flags.isAnonymous) parts.push('<span class="text-red-light">Anonymous</span>');
  return parts.join(', ') || '<span class="text-muted">—</span>';
}

// ── Section: Header ──────────────────────────────────────────────────────────

function buildHeaderSection(caseData, selectedTz, reportType) {
  const now = new Date();
  const reportDate = new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  }).format(now);
  const title = reportType === 'client' ? 'M365 Security Investigation Report' : 'M365 Technical Investigation Report';

  return `
    <div class="report-header">
      <h1>${title}</h1>
      <div class="meta-grid">
        <div>Report generated: <span>${esc(reportDate)}</span></div>
        <div>Account: <span>${esc(caseData.userPrincipalName)}</span></div>
        <div>Reviewed logs: <span>${esc(formatDateOnly(caseData.startDate, selectedTz))} to ${esc(formatDateOnly(caseData.endDate, selectedTz))}</span></div>
        <div>Tenant: <span>${esc(caseData.tenantName || caseData.tenantId)}</span></div>
      </div>
    </div>`;
}

// ── Section: Executive Summary (Client only) ─────────────────────────────────

// ── Episode narrative (v1.183.0) ─────────────────────────────────────────────
// A case can hold more than one compromise, weeks apart, each remediated in turn (a token
// revoke does not start a new episode; only a disable or password reset does). Describing that
// as one continuous window is inaccurate — the account was not exposed the whole time.
const EPISODE_ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth'];

function episodeContainmentPhrase(ep) {
  return ep.containmentType === 'account_disabled' ? 'the account was disabled'
    : ep.containmentType === 'password_reset' ? 'the password was reset'
      : 'sign-in sessions were revoked';
}

function episodeSameDay(ep) {
  return ep.contained && ep.containedAt && String(ep.start).slice(0, 10) === String(ep.containedAt).slice(0, 10);
}

// One sentence per episode: when it began and how it ended.
function episodeSentences(aw, selectedTz) {
  const eps = aw.episodes || [];
  return eps.map((ep, i) => {
    const when = formatTsPlain(ep.start, selectedTz);
    const lead = eps.length > 1
      ? `The ${EPISODE_ORDINALS[i] || `${i + 1}th`} episode began ${when}`
      : `It began ${when}`;
    if (ep.contained && ep.containedAt) {
      const dur = formatDurationShort(ep.start, ep.containedAt);
      return `${lead}, and ${episodeContainmentPhrase(ep)}${dur ? ` about ${dur} later` : ''}.`;
    }
    return `${lead}; no containment action was recorded for it.`;
  });
}

function allEpisodesSameDay(aw) {
  return (aw.episodes || []).length > 0 && (aw.episodes || []).every(episodeSameDay);
}

// The finding's title, the SAME three cases and words as the Executive Brief's
// `Get-ExecBriefTitle` (ExecutiveReportHelper.psm1). `scripts/clientReportBrief.test.mjs` reads
// the PowerShell and fails if the two drift. "Account Compromise" tells a reader someone got
// in, which is the one thing a refused-attempts case rules out.
export const CLIENT_REPORT_TITLES = {
  compromise: 'Microsoft 365 Account Compromise',
  mfaBlocked: 'Microsoft 365 Credential Exposure — Sign-in Blocked by MFA',
  refused: 'Microsoft 365 Account Access Attempt — Sign-ins Refused',
};

export function clientReportTitle(attackerWindow) {
  const aw = attackerWindow || {};
  if (!aw.noTokenIssued) return CLIENT_REPORT_TITLES.compromise;
  const acc = aw.attackerAccess || {};
  if (acc.passwordAccepted && acc.mfaChallenged && !acc.mfaCompleted) return CLIENT_REPORT_TITLES.mfaBlocked;
  return CLIENT_REPORT_TITLES.refused;
}

// ", from N addresses classified malicious" -- the addresses the ATTEMPTS came from (v1.203.0).
// It used to count every classified address in the case. "Classified malicious" only when every
// one of them is: a phishing-kit step can be recorded from the user's own address. Empty on an
// older window that does not carry the list -- saying nothing beats a wrong count.
function attemptAddressPhrase(aw) {
  const ips = aw?.attackerAccess?.attemptIps;
  if (!Array.isArray(ips) || ips.length === 0) return '';
  const mal = new Set(aw.maliciousIPs || []);
  const k = ips.length;
  return `, from ${k} address${k !== 1 ? 'es' : ''}${ips.every((ip) => mal.has(ip)) ? ' classified malicious' : ''}`;
}

function whatHappenedSection(title, text) {
  return `
    <section>
      <h2>What Happened</h2>
      <div class="compromise-box">
        <p><strong>${esc(title)}</strong></p>
        <p>${esc(text)}</p>
      </div>
    </section>`;
}

function buildExecutiveSummary(attackerWindow, ipLookup, selectedTz, noAccess = false) {
  const aw = attackerWindow;
  if (!aw) return '';
  const title = clientReportTitle(aw);

  // 🔴 NO TOKEN WAS ISSUED: every attacker sign-in attempt was refused and nothing else came
  // from the attacker's addresses (utils/attackerAccess.js). Checked FIRST, before the episode
  // and attestation branches, because each of those begins "This account was compromised" or
  // "credentials and session token were captured" -- both false here. The window itself holds
  // no successful activity on such a case, so the dates come from the ATTEMPTS
  // (attemptWindowSpan, the Brief's own span); reading the window printed
  // "This account was compromised on —" (IR-20261026-jturbyville-001).
  if (aw.noTokenIssued) {
    const acc = aw.attackerAccess || {};
    const n = Number(acc.attemptCount) || 0;
    const { first, last } = attemptWindowSpan(aw);
    let when = '';
    if (first && last && first !== last) when = ` between ${formatTsPlain(first, selectedTz)} and ${formatTsPlain(last, selectedTz)}`;
    else if (first || last) when = ` on ${formatTsPlain(first || last, selectedTz)}`;
    const attempts = n === 1 ? 'A sign-in attempt was' : `${n > 0 ? n : 'Several'} sign-in attempts were`;
    let text = `${attempts} made against this account${when}${attemptAddressPhrase(aw)}.`;
    if (title === CLIENT_REPORT_TITLES.mfaBlocked) {
      text += ' The account’s password was entered correctly, but multi-factor authentication was not completed,'
        + ` so ${n === 1 ? 'the attempt was' : 'every attempt was'} refused.`;
    } else {
      text += ` ${n === 1 ? 'The attempt was' : 'Every attempt was'} refused.`;
    }
    // "In the records reviewed", never "the attacker did not get in": the claim is about what
    // the sign-in records show (the Brief's discipline on every negative).
    text += ' The records reviewed show no sign-in token issued to the attacker.';
    return whatHappenedSection(title, text);
  }

  // v1.183.0 — multiple remediation-separated episodes are described as such, not as one span.
  if (aw.multiEpisode) {
    const n = aw.episodeCount;
    let mText = noAccess
      ? `This account's credentials and session token were captured in ${n} separate episodes.`
      : `This account was compromised in ${n} separate episodes.`;
    mText += ' ' + episodeSentences(aw, selectedTz).join(' ');
    if (noAccess) {
      mText += ' The reviewing analyst examined the available activity — including the non-interactive'
        + ' sign-in feed, where token reuse would appear — and found no evidence that the captured access'
        + ' was used to reach the account in any episode.';
    }
    return whatHappenedSection(title, mText);
  }

  const firstTs = formatTsPlain(aw.firstAttackerActivity || aw.firstAttackerSignIn, selectedTz);
  const lastTs = formatTsPlain(aw.lastAttackerActivity || aw.lastAttackerSignIn, selectedTz);
  const duration = formatDurationApprox(aw.firstAttackerActivity || aw.firstAttackerSignIn, aw.lastAttackerActivity || aw.lastAttackerSignIn);

  const lastActivity = aw.lastAttackerActivity || aw.lastAttackerSignIn;
  const firstActivity = aw.firstAttackerActivity || aw.firstAttackerSignIn;
  const hasSpan = lastActivity && lastActivity !== firstActivity;

  // v1.182.0 — the analyst's "credentials captured, no access found" attestation. The
  // window becomes a window of EXPOSURE (the captured session could have been used), and
  // the finding is stated as the analyst's determination — never "the attacker did not
  // access the account", which the records cannot prove.
  let text;
  if (noAccess) {
    text = `This account's credentials and session token were captured on ${firstTs}`;
    if (hasSpan) text += `, opening a window of exposure of ${duration} (through ${lastTs})`;
    text += '. The reviewing analyst examined the available activity — including the non-interactive'
      + ' sign-in feed, where token reuse would appear — and found no evidence that the captured access'
      + ' was used to reach the account.';
  } else {
    text = `This account was compromised on ${firstTs}`;
    if (hasSpan) text += ` and the attacker had access for ${duration} (last attacker access ${lastTs})`;
    text += '.';
  }

  return whatHappenedSection(title, text);
}

// ── Section: How It Started (Client only) ────────────────────────────────────
// Stated only when the analyst marked NO entry point (no phishing flag, no confirmed or
// suspected URL click) -- the Brief's `Format-ExecBriefPhishingEntry` absent case, same words.
// A missing section would read as an oversight; "was not established" is never "there was
// none", because the analyst not identifying an entry point is not evidence that none exists.
// When a marker exists, the URL-click and phishing-campaign sections below carry it.
//
// 🔴 v1.203.0: ALSO WHEN IT WAS ESTABLISHED. Only the absent case was ported, so on 14 of 41
// cases the Brief said "Lure message identified by the analyst … Malicious link clicked …" and
// the Client Report had no such section at all. Same sentences as `Format-ExecBriefPhishingEntry`.
//
// 🔴 THE URL-CLICK CLAUSE (Get-ExecBriefUrlClickClause). Where the run holds no URL-click rows,
// "the analyst identified no malicious link click" and "link clicks cannot be seen" are the same
// sentence and only one is what happened, so the clause REPLACES the negative click finding.
// Mirrors the server's rule on the same inputs: failed = the run's url_click query errored;
// reviewed = the run holds URL-click rows; anything else = no data.
export const URL_CLICK_CLAUSES = {
  failed: 'the URL-click log could not be read for this investigation',
  none: 'no URL-click data is available, potentially because of a lack of licensing or a lack of configuration',
};
export function urlClickClause(results) {
  const errs = results?.DataSourceErrors || [];
  if (errs.some((e) => String(e?.workload || e?.Workload || '').toLowerCase() === 'url_click')) return URL_CLICK_CLAUSES.failed;
  if ((results?.UrlClickEvents || []).length > 0) return '';
  return URL_CLICK_CLAUSES.none;
}

function buildEntryPointStatement(pack, results, selectedTz, releaseHtml = '') {
  if (!pack) return '';
  const entry = pack.phishing_entry_point;
  const clause = urlClickClause(results);
  let body;
  if (!entry) {
    body = clause
      ? `<p><strong>Not established</strong> from the records reviewed. No lure message was identified, and ${esc(clause)}.</p>`
      : '<p><strong>Not established</strong> from the records reviewed. The reviewing analyst identified no lure message and no malicious link click.</p>';
  } else {
    // Times from the run's own events (by id), so they read like every other time here.
    const timeOf = (list, id, fallback) => {
      const ev = (list || []).find((e) => e?.EventId === id);
      return ev?.EventTimeUTC ? formatTsPlain(ev.EventTimeUTC, selectedTz) : (fallback || '');
    };
    const emails = entry.emails || [];
    const clicks = entry.clicks || [];
    const lines = [];
    if (emails.length) {
      const when = timeOf(results?.MessageTraceEvents, emails[0].eventId, emails[0].timeDisplay);
      lines.push(`<p><strong>Lure message</strong> identified by the analyst${emails.length > 1 ? ` (${emails.length})` : ''}${when ? `, first arriving ${esc(when)}` : ''}.</p>`);
    }
    if (clicks.length) {
      const when = timeOf(results?.UrlClickEvents, clicks[0].eventId, clicks[0].timeDisplay);
      const emailIds = new Set(emails.map((e) => e.eventId));
      const allLinked = emails.length > 0 && clicks.every((c) => emailIds.has(c.fromEmailEventId));
      lines.push(`<p><strong>${allLinked ? 'Malicious link in that message clicked' : 'Malicious link clicked'}</strong> from this account, confirmed by the analyst${clicks.length > 1 ? ` (${clicks.length})` : ''}${when ? `, first at ${esc(when)}` : ''}.</p>`);
    }
    if (clause) lines.push(`<p>${esc(clause.charAt(0).toUpperCase() + clause.slice(1))}.</p>`);
    body = lines.join('\n      ');
  }
  return `
    <section>
      <h2>How It Started</h2>
      ${body}
      ${releaseHtml}
    </section>`;
}

// ── Section: What the Attacker Did (Client only) ─────────────────────────────
// The Executive Brief's nine-line checklist, from the SAME derivation
// (`buildAttackerActions` via `buildExecReportFactPack`), so the two documents cannot answer
// "did the attacker read mail?" differently. Replaces the Key Findings counts it used to print
// ("133 email access events were recorded"), which had no attribution test at all and sat under
// an attacker heading on a case where the attacker read nothing.
//
// Three answers that must not look alike: "Yes" (attributed), "No" (the source could have
// matched and nothing did), "Not determined" (it could not -- or its query failed). Server-only
// refinements the Brief adds after reconcile (the coverage qualifier "No correlating evidence
// found") are not available here; this states the plain negative with the same basis note.
const CHECKLIST_BASIS_NOUNS = [
  ['ip', 'by attacker address'],
  ['analyst', 'by analyst review'],
  ['device', 'by attacker device'],
  ['session', 'by shared session'],
];

export function checklistAnswer(row) {
  const count = Number(row?.count) || 0;
  const mix = row?.basisMix || {};
  const unit = row?.id === 'enterprise_apps' ? 'application' : 'event';
  if (row?.state === 'yes') {
    const sessionOnly = count > 0 && (Number(mix.session) || 0) === count;
    const answer = `${sessionOnly ? 'Observed in a compromised session' : 'Yes'} · ${count} ${unit}${count !== 1 ? 's' : ''}`;
    let basis = row.id === 'enterprise_apps'
      ? 'distinct applications, not events'
      : CHECKLIST_BASIS_NOUNS.filter(([k]) => (Number(mix[k]) || 0) > 0).map(([k, w]) => `${mix[k]} ${w}`).join(', ');
    if (sessionOnly) basis += ' — corroboration, not proof';
    return { answer, basis };
  }
  if (row?.state === 'not_determined') {
    return { answer: 'Not determined', basis: row.note || 'the records cannot support this claim in either direction' };
  }
  return { answer: 'No', basis: row?.note || 'reviewed, and nothing was attributed to the attacker' };
}

function buildAttackerChecklist(pack) {
  const rows = (pack?.attacker_actions || []).filter(Boolean);
  if (!rows.length) return '';
  const body = rows.map((r) => {
    const { answer, basis } = checklistAnswer(r);
    return `<tr>
      <td>${esc(r.label || r.id)}</td>
      <td class="nowrap">${esc(answer)}</td>
      <td>${esc(basis || '—')}</td>
    </tr>`;
  }).join('');
  return `
    <section>
      <h2>What the Attacker Did</h2>
      <table class="data-table">
        <thead><tr><th>What the attacker did</th><th>Answer</th><th>Based on the incident-related records</th></tr></thead>
        <tbody>${body}</tbody>
      </table>
    </section>`;
}

// ── Section: How the Account Was Compromised — Phishing-Kit Sign-In ──────────
// The sign-in legs the "AiTM Landing Sign-In" detection fires on — recovered
// raw-UAL rows (UnattributedRecovery) or a URL-shaped ApplicationId (the kit's
// landing host). Critically, the IP on these rows is the USER'S OWN address:
// the victim was relayed through the attacker's proxy, so their real IP appears
// on the record. They are therefore NOT attacker-IP activity and must not be
// read as such — they are the moment the password and an active session/refresh
// token were captured, which is how the attacker then operated without a fresh
// sign-in.
//
// 🔴 ROWS = the window's CAPTURE legs only (v1.188.0): a URL-shaped application, the one
// signal that proves the password and a session token were captured. It used to select every
// `isPinnedSignInEvent` row, which also matches Microsoft's ordinary MFA sub-steps
// (UnattributedRecovery) — so on IR-20260930-mendozap-001 this section, under a heading
// asserting credential capture, listed 61 rows of which 52 were the victim's own MFA pushes.
// A recovered MFA leg that anchors the window is reported in Key Findings instead, in words
// that do not claim capture.
//
// 🔴 The address label follows the ANALYST'S CLASSIFICATION, never assumed: a capture leg is
// usually on the user's own address, but when the analyst classified it malicious the report
// must not call it "the user's own".
function buildAiTMLandingSection(combinedEvents, ctx, selectedTz, attackerWindow) {
  const captureIds = attackerWindow?.captureLegIds || new Set();
  const legs = (combinedEvents || []).filter(
    (e) => e && e._eventType === 'SignIn' && captureIds.has(e.EventId),
  );
  if (legs.length === 0) return '';
  legs.sort((a, b) => new Date(a.EventTimeUTC) - new Date(b.EventTimeUTC));

  const classes = ctx?.ipClassifications || {};
  const isMal = (ip) => classes[ip] === 'malicious';
  const anyMalicious = legs.some((e) => isMal(e.IPAddress || e.InferredIP || ''));
  const allMalicious = legs.every((e) => isMal(e.IPAddress || e.InferredIP || ''));
  // v1.194.2: every kit row is an MFA step of an attempt Conditional Access REFUSED, and no
  // sign-in from the kit's addresses issued a token (utils/refusedAttempts.js). Then no session
  // token was captured, and an unclassified address cannot be asserted to be the user's own --
  // on IR-20261013-levig-001 the kit's addresses were its own proxies.
  const noToken = attackerWindow?.noTokenIssued === true;

  const rows = legs.map((evt) => {
    const app = rowAppLabel(evt);
    const ip = evt.IPAddress || evt.InferredIP || '';
    const status = evt.ResultStatus || evt.Status || '';
    const ipCell = !ip ? '' : isMal(ip)
      ? `<span class="ip-malicious">${esc(ip)}</span> <span style="color:#94a3b8;font-size:10px;white-space:nowrap;">(classified malicious)</span>`
      : noToken
        ? esc(ip)
        : `${esc(ip)} <span style="color:#94a3b8;font-size:10px;white-space:nowrap;">(user’s own address)</span>`;
    return `<tr>
      <td class="nowrap">${esc(formatTs(evt.EventTimeUTC, selectedTz))}</td>
      <td>${esc(app)}</td>
      <td class="font-mono">${ipCell}</td>
      <td>${esc(status)}</td>
    </tr>`;
  }).join('');

  const many = legs.length > 1;
  if (noToken) {
    return `
    <section>
      <h2>How the Account Was Targeted — Phishing-Kit Sign-In Attempts</h2>
      <div class="compromise-box">
        <p>The sign-in step${many ? 's' : ''} below went through an attacker-in-the-middle (AiTM) phishing kit:
        the user entered their password into the kit and completed multi-factor authentication through it.
        <strong>Conditional Access refused every attempt, so no session token was issued</strong>, and these
        attempts did not give the attacker access to the account. Treat the user’s password as compromised and
        reset it, and revoke the user’s sessions as a precaution.</p>
      </div>
      <table class="data-table">
        <thead><tr><th>Time</th><th>Phishing-kit application</th><th>Signed in from</th><th>Result</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </section>`;
  }
  return `
    <section>
      <h2>How the Account Was Compromised — Phishing-Kit Sign-In</h2>
      <div class="compromise-box">
        <p>The sign-in${many ? 's' : ''} below ${many ? 'were' : 'was'} completed through an
        attacker-in-the-middle (AiTM) phishing kit. ${allMalicious
    ? `The address${many ? 'es were' : ' was'} classified malicious by the reviewing analyst.`
    : anyMalicious
      ? 'Where an address is marked <strong>user’s own address</strong>, the user was relayed through the attacker’s proxy, so their real IP appears on the record and it is <strong>not an attacker-controlled address</strong>; where it is marked classified malicious, the reviewing analyst attributed it to the attacker.'
      : `The address shown is the <strong>user’s own network address</strong> — the user was relayed through the attacker’s
        proxy, so their real IP appears on the record. <strong>${many ? 'These are' : 'This is'} not attacker-controlled IP
        address${many ? 'es' : ''}.</strong>`} ${many ? 'They are' : 'It is'} the point at which the user’s password and an active
        session/refresh token were captured, which is how the attacker was then able to act on the
        account without triggering a new sign-in. Treat the account’s credentials and all active
        sessions as compromised from this time.</p>
      </div>
      <table class="data-table">
        <thead><tr><th>Time</th><th>Phishing-kit application</th><th>Signed in from</th><th>Result</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </section>`;
}

// ── Section: Attacker Origin (Client only) ───────────────────────────────────

// A failed row. ResultStatus may say "Success" when Operation is "UserLoginFailed" (M365 audit
// log inconsistency), so both fields are checked. Shared by Attacker Origin and the Key Findings
// sign-in bullet so "signed in N times from M addresses" counts the same addresses the table lists.
function isFailedRow(evt) {
  const status = (evt?.ResultStatus || evt?.Status || '').toLowerCase();
  const op = (evt?.Operation || '').toLowerCase();
  return status === 'failed' || status === 'failure' || op.includes('failed');
}

function buildAttackerOrigin(attackerWindow, ipLookup, combinedEvents, ipComments = {}, nonInteractiveEvents = []) {
  const aw = attackerWindow;
  if (!aw || !aw.maliciousIPs.length) return '';

  // Only show malicious IPs that had actual successful activity in the events.
  // Exclude failed sign-ins — ResultStatus may say "Success" when Operation is
  // "UserLoginFailed" (M365 audit log inconsistency), so check both fields.
  const activeIPs = new Set();
  for (const evt of combinedEvents) {
    // 🔴 Non-interactive rows are handled by the niAgg pass below and MUST be skipped
    // here. They carry `Outcome`, not `ResultStatus`, so this loop's failure test cannot
    // see their outcome at all -- once the feed joined `combinedEvents` (v1.156.0) a
    // failure-ONLY address would have been admitted as "active", which is exactly the
    // operator rule this table exists under ("i don't use failed attempts from IPs as
    // confirmation"). The niAgg pass applies that rule on the feed's own counts.
    if (evt._eventType === 'NonInteractiveSignIn') continue;
    if (isFailedRow(evt)) continue;
    if (evt.IPAddress) activeIPs.add(evt.IPAddress);
    if (evt.InferredIP) activeIPs.add(evt.InferredIP);
  }
  // Non-interactive activity counts as activity. `combinedEvents` is built from the
  // interactive feeds only, so without this a malicious address whose whole footprint is
  // token refresh was silently dropped from this table -- on the reference case that was
  // 43.166.208.95 with 224 SUCCESSFUL authentications, plus 216.203.13.24/.26.
  // Success-only, which is the same rule the loop above applies: a rejected attempt is
  // not evidence the address was used.
  const niAgg = nonInteractiveIpAggregates(nonInteractiveEvents);
  for (const a of niAgg.values()) {
    if (a.successCount > 0) activeIPs.add(a.ipAddress);
  }
  const visibleIPs = aw.maliciousIPs.filter(ip => activeIPs.has(ip));
  if (visibleIPs.length === 0) return '';

  const rows = visibleIPs.map(ip => {
    const info = ipLookup[ip] || {};
    const loc = [info.City, info.State, info.Country].filter(Boolean).join(', ') || 'Unknown';
    // An address known only from the non-interactive feed has no enrichment row, so fall
    // back to the ASN the feed itself carries rather than printing "Unknown" over data we
    // actually have. Reported as a bare AS number -- never invented as a provider name.
    const niAsn = [...(niAgg.get(ip)?.asns || [])][0] || '';
    const provider = info.Provider || (niAsn ? `AS${niAsn}` : 'Unknown');
    return `<tr>
      <td class="ip-malicious font-mono">${esc(ip)}</td>
      <td>${esc(ipComments[ip] || '')}</td>
      <td>${esc(loc)}</td>
      <td>${esc(provider)}</td>
    </tr>`;
  }).join('');

  return `
    <section>
      <h2>Attacker Origin</h2>
      <table class="data-table">
        <thead><tr><th>IP Address</th><th>Comment</th><th>Location</th><th>Internet Provider</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </section>`;
}

// ── Section: Key Findings (Client only) ──────────────────────────────────────

function buildKeyFindings(combinedEvents, attackerWindow, noAccess = false) {
  const aw = attackerWindow;
  if (!aw) return '';

  const maliciousSet = new Set(aw.maliciousIPs);
  const bullets = [];

  // v1.183.0 — multiple contained episodes stated up front, so a reader is not left to infer
  // continuous access from the window dates.
  if (aw.multiEpisode) {
    const sameDay = allEpisodesSameDay(aw) ? ', each contained the same day' : '';
    bullets.push(`The account was compromised in ${aw.episodeCount} separate episodes${sameDay} — see “What Happened” for the sequence. The activity was not continuous between them.`);
  }

  // v1.182.0 — the analyst's "no attacker access found" attestation, stated first because on
  // this case it is the headline finding: the exposure is the captured credentials/session.
  if (noAccess) {
    bullets.push('No evidence of attacker access to the account was found in the reviewed activity — interactive and non-interactive sign-ins, and every address classified as the user’s own. What was compromised is the credentials and session token captured at the phishing-kit sign-in.');
  }

  // The phishing-kit landing (v1.181.0). Stated first because on a pure AiTM case it is the
  // whole story of how access was obtained, and the malicious-address bullets below are all
  // no-ops when no IP is classified. The address is deliberately not named here: it is the
  // user's own, and "How the Account Was Compromised" carries the detail.
  const kitLegs = Number(aw.successfulPhishingKitLegCount) || 0;
  if (kitLegs > 0 && aw.noTokenIssued) {
    // v1.194.2: the kit's attempts were all refused -- "accessed" would be false.
    bullets.push(`An attacker-in-the-middle phishing kit relayed the user’s sign-in (${kitLegs} sign-in step${kitLegs !== 1 ? 's' : ''}), but Conditional Access refused every attempt and no session token was issued (see “How the Account Was Targeted”).`);
  } else if (aw.noTokenIssued) {
    // v1.194.7: the same fact for any window, not only a kit-anchored one (attackerAccess.js).
    const n = Number(aw.attackerAccess?.attemptCount) || 0;
    bullets.push(`The attacker made ${n} sign-in attempt${n !== 1 ? 's' : ''}${attemptAddressPhrase(aw)}, and every attempt was refused; the records reviewed show no sign-in token issued to the attacker.`);
  } else if (kitLegs > 0) {
    bullets.push(`The account was accessed through an attacker-in-the-middle phishing-kit sign-in on ${kitLegs} occasion${kitLegs !== 1 ? 's' : ''} (see “How the Account Was Compromised”).`);
  }

  // Recovered MFA steps that anchor the window (v1.188.0). Microsoft logs MFA sub-steps
  // without a username; these few were attributed to the attacker by an EXACT link to their
  // own sign-in transaction. 🔴 Stated as attribution, NEVER as credential capture — only a
  // phishing-kit application supports that claim, and it has its own bullet above.
  // Not when no token was issued: those steps ARE the refused attempts' own MFA records, so
  // "38 multi-factor sign-in steps ... attributed to the attacker" beside "6 sign-in attempts"
  // reads as a second, larger count of the same thing (IR-20261021-ehribik-001).
  const recAnchors = aw.noTokenIssued ? 0 : (Number(aw.successfulRecoveredLegAnchorCount) || 0);
  if (recAnchors > 0) {
    const ev = aw.recoveredLegEvidence || {};
    const why = [];
    if (ev.leg_ip_malicious || ev.tx_ip_malicious) why.push('ran from an address classified malicious');
    if (ev.tx_login_multi_ip) why.push('started from more than one address within seconds, the pattern of a relaying proxy');
    if (ev.tx_attacker_session) why.push('belonged to a session that was also used from a malicious address');
    const reason = why.length ? ` because their sign-in transaction ${why.join(', or ')}` : '';
    bullets.push(`${recAnchors} multi-factor sign-in step${recAnchors !== 1 ? 's' : ''} that Microsoft logged without a username ${recAnchors !== 1 ? 'were' : 'was'} attributed to the attacker${reason}.`);
  }

  // Sign-in count from malicious IPs.
  // 🔴 Counted through underlyingEventCount, NOT by row: sign-ins are rolled into access
  // periods for display, so `.length` here reported "signed in 1 time" for a period that
  // held 38. Same for the app set -- a period row's own ApplicationName names ONE of the
  // applications it spans.
  // 🔴 NOT when no token was issued. The attempts bullet above already states that every
  // attempt was refused, and these rows are the attempts' own MFA-wait steps, so "The attacker
  // signed in 8 times" directly after it was one report contradicting itself
  // (IR-20261026-jturbyville-001: 4 attempts, 8 step records). Same for the applications
  // bullet: a refused attempt names the application it ASKED for, not one it reached.
  const noToken = aw.noTokenIssued === true;
  // Successful sign-ins only: "signed in" is a claim that the sign-in worked, and Attacker Origin
  // lists only addresses with successful activity (isFailedRow is the shared test).
  const attackerSignIns = combinedEvents.filter(e => e._eventType === 'SignIn' && maliciousSet.has(e.IPAddress) && !isFailedRow(e));
  const attackerSignInCount = noToken ? 0 : underlyingEventTotal(attackerSignIns);
  if (attackerSignInCount > 0) {
    // 🔴 The addresses THESE sign-ins came from, not every address classified malicious in the
    // case (v1.203.0): "signed in 1 time from 12 IP addresses" on IR-20261014-lperry-001, whose
    // own Attacker Origin table listed 2.
    const ipCount = new Set(attackerSignIns.map((e) => e.IPAddress).filter(Boolean)).size;
    bullets.push(`The attacker signed in ${attackerSignInCount} time${attackerSignInCount !== 1 ? 's' : ''} from ${ipCount} IP address${ipCount !== 1 ? 'es' : ''}.`);
  }

  // Non-interactive authentications from malicious IPs. This is token replay -- the
  // attacker re-using a stolen refresh token without signing in again -- and it was
  // absent from every report until v1.156.0 while being on screen in All Events.
  const attackerTokenUse = combinedEvents.filter(e => e._eventType === 'NonInteractiveSignIn' && maliciousSet.has(e.IPAddress));
  const attackerTokenCount = attackerTokenUse.reduce((n, e) => n + (Number(e._periodAuthCount) || Number(e.EventCount) || 0), 0);
  if (attackerTokenCount > 0) {
    bullets.push(`${attackerTokenCount} non-interactive authentication${attackerTokenCount !== 1 ? 's' : ''} (token re-use, without a fresh sign-in) came from the same addresses.`);
  }

  // ⛔ NO APPLICATIONS BULLET (removed v1.203.0). It counted applications on interactive
  // sign-ins only, so it disagreed with the checklist's "Used applications" line in the same
  // report on 10 of 41 cases (IR-20260818-Controller-001: 5 here, 13 there). The checklist
  // line is the Brief's figure and carries the application count.

  // ⛔ NO MAIL / FILE / RULE COUNTS HERE. This list used to print "133 email access events
  // were recorded", "2 emails were sent from this account" and file counts with NO attribution
  // test, under Key Findings, on a case where the attacker read and sent nothing (a count is
  // not an attribution). Those answers now come from "What the Attacker Did", the Brief's
  // attribution-gated checklist; the unattributed activity stays in Detailed Findings.

  // v1.194.7 — what remains exposed, and an account that is enabled again. Same conditions and
  // wording as the Executive Brief (utils/attackerAccess.js buildExposures, operator-approved
  // 2026-10-15): each is stated only when the records support it.
  for (const x of buildExposures({ attackerWindow: aw })) {
    if (x.id === 'password_known') {
      bullets.push('The attacker has the account’s password: it was entered correctly during the sign-in attempts, and no password change is recorded after that.');
    } else if (x.id === 'mfa_approved') {
      bullets.push(`The user approved ${x.push ? 'an MFA push' : 'a multi-factor authentication request'} they did not start${x.ip ? ` (sign-in attempt from ${x.ip})` : ''}.`);
    }
  }
  if (aw.accountState?.finalState === 'enabled' && aw.accountState?.reenabled) {
    bullets.push(`The account was enabled again after it was disabled${aw.accountState.viaDirectorySync ? ', by directory synchronization from on-premises Active Directory, which overrides a disable made in the cloud' : ''}; it was enabled at the end of the reviewed records.`);
  }

  if (bullets.length === 0) return '';

  return `
    <section>
      <h2>Key Findings</h2>
      <ul class="findings-list">
        ${bullets.map(b => `<li>${esc(b)}</li>`).join('\n        ')}
      </ul>
    </section>`;
}

// ── Section: Remediation (Client only) ───────────────────────────────────────

// The Brief's "When it was contained" (`Format-ExecBriefContainment`), in this report's words.
//
// 🔴 EVERY OCCURRENCE, not the earliest of each type. The window's `*At` fields are the FIRST
// of each action, so this section showed one revoke and one reset on
// IR-20261026-jturbyville-001 where the records hold two revokes and two password changes on
// Oct 25 and two more on Oct 26 -- the Brief beside it listed all of them.
//
// 🔴 A MISSING ACTION IS STATED, NOT OMITTED. Three fixed rows, always: an absent row reads as
// "this section is shorter", when what it means is "we have no record of this action". Worded
// "no record in the reviewed logs", never "this did not happen".
//
// 🔴 A DISABLE THAT WAS UNDONE IS NOT CONTAINMENT. With an enabled/disabled history the account
// row lists every change in order and states the account's state at the end of the records.
const isSyncActor = (a) => /^(ConnectSyncProvisioning_|Sync_)/i.test(a || '');
const actorLabel = (a) => (isSyncActor(a) ? 'directory synchronization' : a);

function containmentOccurrences(aw, type, selectedTz) {
  const occ = (aw.containmentEvents || [])
    .filter((c) => c && c.type === type && c.time)
    .sort((a, b) => String(a.time).localeCompare(String(b.time)));
  const lines = [];
  const seen = new Set();
  for (const c of occ) {
    // Microsoft records one action as several audit events in the same minute.
    const when = formatTsPlain(c.time, selectedTz);
    if (seen.has(when)) continue;
    seen.add(when);
    lines.push(`${when}${c.actor ? ` by ${actorLabel(c.actor)}` : ''}`);
  }
  return lines;
}

function buildRemediation(attackerWindow, selectedTz) {
  const aw = attackerWindow;
  if (!aw) return '';

  // Pre-containmentEvents windows fall back to the earliest-of-type field.
  const fallback = (at, by) => (at ? [`${formatTsPlain(at, selectedTz)}${by ? ` by ${actorLabel(by)}` : ''}`] : []);
  const occurrences = (type, at, by) => {
    const lines = containmentOccurrences(aw, type, selectedTz);
    return lines.length ? lines : fallback(at, by);
  };

  const rows = [];
  const changes = (aw.accountState?.changes || []).filter((c) => c && c.time);
  if (changes.length) {
    const lines = changes.map((c) => `${c.type === 'enabled' ? 'Enabled' : 'Disabled'} ${formatTsPlain(c.time, selectedTz)}${c.actor ? ` by ${actorLabel(c.actor)}` : ''}`);
    const final = aw.accountState.finalState === 'disabled'
      ? 'The account was disabled at the end of the reviewed records.'
      : 'The account was enabled at the end of the reviewed records.';
    rows.push({ label: 'Account disabled', lines: [...lines, final] });
  } else {
    rows.push({ label: 'Account disabled', lines: occurrences('account_disabled', aw.accountDisabledAt, aw.accountDisabledBy) });
  }
  rows.push({ label: 'Sign-in sessions revoked', lines: occurrences('tokens_revoked', aw.tokensRevokedAt, aw.tokensRevokedBy) });
  rows.push({ label: 'Password changed', lines: occurrences('password_reset', aw.passwordResetAt, aw.passwordResetBy) });

  const items = rows.map((r) => {
    const detail = r.lines.length
      ? r.lines.map((l) => `<div>${esc(l)}</div>`).join('')
      : '<div>No record in the reviewed logs.</div>';
    return `<li><strong>${esc(r.label)}</strong>${detail}</li>`;
  });

  return `
    <section>
      <h2>Actions Taken</h2>
      <div class="remediation-box">
        <ul class="findings-list">
          ${items.join('\n          ')}
        </ul>
      </div>
    </section>`;
}

// ── Section: Enterprise Apps Simple (Client only) ────────────────────────────

function buildEnterpriseAppsSimple(apps) {
  if (!apps || apps.length === 0) return '';
  const names = [...new Set(apps.map(a => a.displayName || a.appId).filter(Boolean))].sort();
  return `
    <section>
      <h2>Applications That Accessed This Account</h2>
      <ul class="app-list">
        ${names.map(n => `<li>${esc(n)}</li>`).join('\n        ')}
      </ul>
    </section>`;
}

// ── Section: Compromise Summary (Technical only) ─────────────────────────────

function buildCompromiseSummary(attackerWindow, ipLookup, selectedTz, noAccess = false) {
  const aw = attackerWindow;
  if (!aw) return '';

  const firstActivity = aw.firstAttackerActivity || aw.firstAttackerSignIn;
  const lastActivity = aw.lastAttackerActivity || aw.lastAttackerSignIn;
  const duration = formatDuration(firstActivity, lastActivity);
  const ips = aw.maliciousIPs.map(ip => {
    const info = ipLookup[ip] || {};
    const loc = [info.City, info.Country].filter(Boolean).join(', ');
    return `<span class="ip-malicious font-mono">${esc(ip)}</span>${loc ? ` (${esc(loc)})` : ''}`;
  }).join(', ');

  // v1.182.0 — under the "no access found" attestation the first two lines describe a window
  // of EXPOSURE, not confirmed access; the remaining derivation lines are unchanged.
  // v1.183.0 — a case with multiple remediation-separated episodes lists them instead of a
  // single span, so the dates never read as continuous access.
  const kitLegLabel = aw.noTokenIssued && aw.anchoredByPhishingKit
    ? 'Phishing-kit sign-in steps (all refused by Conditional Access)'
    : aw.noTokenIssued ? 'Successful attacker sign-ins (every attempt was refused)'
      : aw.anchoredByPhishingKit ? 'Successful phishing-kit sign-ins' : 'Successful attacker sign-ins';
  let openLines;
  if (aw.noTokenIssued && !noAccess && !aw.multiEpisode) {
    // v1.194.7 (pipeline accuracy review): every attacker sign-in attempt was refused and no
    // token was issued. "Account compromised at … lasted …" / "Last attacker access" would assert
    // access that never happened.
    const n = Number(aw.attackerAccess?.attemptCount) || 0;
    openLines = `
        <p>Attacker sign-in attempts began at <strong>${esc(formatTs(firstActivity, selectedTz))}</strong>${aw.firstAttackerIP ? ` from <span class="ip-malicious font-mono">${esc(aw.firstAttackerIP)}</span>` : ''} and ran for ${esc(duration)}.</p>
        <p>${n > 1 ? `Each of the ${n} attempts` : 'The attempt'} was refused; the records reviewed show no sign-in token issued to the attacker. Last attempt: <strong>${esc(formatTs(lastActivity, selectedTz))}</strong>${aw.lastAttackerIP ? ` from <span class="ip-malicious font-mono">${esc(aw.lastAttackerIP)}</span>` : ''}</p>`;
  } else if (aw.multiEpisode) {
    const items = (aw.episodes || []).map((ep) => {
      const dur = ep.containedAt ? formatDurationShort(ep.start, ep.containedAt) : '';
      const tail = ep.contained
        ? ` — ${episodeContainmentPhrase(ep)}${dur ? ` about ${dur} later (${esc(formatTs(ep.containedAt, selectedTz))})` : ''}`
        : ' — no containment action recorded';
      return `<li><strong>${esc(formatTs(ep.start, selectedTz))}</strong>${tail}</li>`;
    }).join('\n          ');
    const verb = noAccess ? 'Credentials/session captured in' : 'Compromised in';
    openLines = `
        <p>${verb} <strong>${aw.episodeCount}</strong> separate episodes — the activity was not continuous between them:</p>
        <ul class="findings-list">
          ${items}
        </ul>${noAccess ? '\n        <p>No attacker access to the account was observed in the reviewed activity (interactive and non-interactive sign-ins) in any episode.</p>' : ''}`;
  } else if (noAccess) {
    openLines = `
        <p>Credentials/session captured at <strong>${esc(formatTs(firstActivity, selectedTz))}</strong>; window of exposure ${esc(duration)}.</p>
        <p>End of exposure window: <strong>${esc(formatTs(lastActivity, selectedTz))}</strong>. No attacker access to the account was observed in the reviewed activity (interactive and non-interactive sign-ins).</p>`;
  } else {
    openLines = `
        <p>Account compromised at <strong>${esc(formatTs(firstActivity, selectedTz))}</strong>${aw.firstAttackerIP ? ` from <span class="ip-malicious font-mono">${esc(aw.firstAttackerIP)}</span>` : ''} and lasted ${esc(duration)}.</p>
        <p>Last attacker access: <strong>${esc(formatTs(lastActivity, selectedTz))}</strong>${aw.lastAttackerIP ? ` from <span class="ip-malicious font-mono">${esc(aw.lastAttackerIP)}</span>` : ''}</p>`;
  }

  let html = `
    <section>
      <h2>Compromise Summary</h2>
      <div class="compromise-box">${openLines}
        <p>Malicious IPs: ${ips || (aw.noTokenIssued
          ? 'none classified — the phishing kit’s sign-in attempts were all refused by Conditional Access and no session token was issued (see “How the Account Was Targeted”)'
          : aw.anchoredByPhishingKit
          ? 'none classified — access was obtained through a phishing-kit sign-in on the user’s own address (see “How the Account Was Compromised”)'
          : aw.anchorBasis === 'recovered_transaction'
            ? 'none classified — the window rests on multi-factor sign-in steps whose sign-in transaction started from more than one address within seconds (see Key Findings)'
            : aw.anchorBasis === 'analyst_session'
              ? 'none classified — the window rests on a sign-in session the reviewing analyst identified as the attacker’s, from the sign-in that created it'
              : 'none')}</p>
        <p>Global cutoff: <strong>${esc(formatTs(aw.globalCutoff, selectedTz))}</strong> — ${esc(aw.cutoffMethod || '')}</p>
        <p>Token lifetime used: ${aw.tokenLifetimeUsed} minutes</p>
        <p>${esc(kitLegLabel)}: ${aw.successfulAttackerSignInCount}</p>`;

  const remItems = [];
  if (aw.accountDisabledAt) remItems.push({ ts: aw.accountDisabledAt, html: `<p>Account disabled: ${esc(formatTs(aw.accountDisabledAt, selectedTz))}${aw.accountDisabledBy ? ` by ${esc(aw.accountDisabledBy)}` : ''}</p>` });
  for (const c of (aw.accountState?.changes || [])) {
    if (c.type === 'enabled') remItems.push({ ts: c.time, html: `<p>Account re-enabled: ${esc(formatTs(c.time, selectedTz))}${c.actor ? ` by ${esc(c.actor)}` : ''}</p>` });
  }
  if (aw.tokensRevokedAt) remItems.push({ ts: aw.tokensRevokedAt, html: `<p>Tokens revoked: ${esc(formatTs(aw.tokensRevokedAt, selectedTz))}${aw.tokensRevokedBy ? ` by ${esc(aw.tokensRevokedBy)}` : ''}</p>` });
  if (aw.passwordResetAt) remItems.push({ ts: aw.passwordResetAt, html: `<p>Password reset: ${esc(formatTs(aw.passwordResetAt, selectedTz))}${aw.passwordResetBy ? ` by ${esc(aw.passwordResetBy)}` : ''}</p>` });
  if (remItems.length > 0) {
    remItems.sort((a, b) => new Date(a.ts) - new Date(b.ts));
    html += `<div class="remediation-box" style="margin-top:12px">`;
    html += remItems.map(i => i.html).join('');
    html += `</div>`;
  }

  html += `</div></section>`;
  return html;
}

// ── Section: Confirmed Malicious URL Clicks ──────────────────────────────────

function buildConfirmedUrlClicks(results, urlClassifications, selectedTz) {
  const allClicks = results.UrlClickEvents || [];
  const confirmed = allClicks.filter(evt => urlClassifications[evt.EventId] === 'confirmed');
  if (confirmed.length === 0) return '';

  const rows = confirmed.map(evt => `
    <tr class="row-malicious">
      <td>${esc(formatTs(evt.EventTimeUTC, selectedTz))}</td>
      <td>${esc(evt.ActionType || '')}</td>
      <td class="url-cell ip-malicious">${esc(evt.Url || '')}</td>
      <td>${esc(evt.IPAddress || '')}</td>
      <td>${esc(evt.ThreatType || '')}</td>
      <td class="text-tiny">${esc(evt.NetworkMessageId || '')}</td>
    </tr>`).join('');

  return `
    <section>
      <h2>Confirmed Malicious URL Clicks</h2>
      <table class="data-table">
        <thead><tr><th>Timestamp</th><th>Action</th><th>URL</th><th>Client IP</th><th>Threat</th><th>Message ID</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </section>`;
}

/**
 * "Other accounts to review" — the Client Report's phishing-campaign section.
 *
 * TWO findings, both about the flagged phishing email and neither of them a compromise
 * claim about anybody else:
 *
 *   1. The account holder RELEASED the lure from quarantine (and, when the audit record
 *      says so, reported it to Microsoft as a false positive). Microsoft had stopped the
 *      message; a person took it back out. On IR-20260902-tthomason-001 that is the single
 *      most consequential act in the case.
 *   2. The same sender also delivered mail to OTHER accounts in the tenant.
 *
 * 🔴 (2) IS A LEAD, AND THE WORDING IS THE CONTRACT (operator, 2026-09-02). The runbook's
 * Related Recipients query is SENDER-WIDE over the case date range — no subject, no
 * campaign constraint — and on the reference case the other recipients' message is a
 * DIFFERENT message instance from the investigated user's (same sender and subject,
 * distinct RFC message-ids). So: "mail from the flagged sender", never "the same email";
 * "accounts to review", never "were also phished"; and the non-assertion sentence is part
 * of the finding, not decoration.
 *
 * 🔴 "Not released" IS NEVER RENDERED for another account. Their release action is outside
 * this case's collection scope, so absence of a release event is not evidence of no
 * release — hence an em-dash, never "No".
 *
 * 🔴 IT READS THE RAW `results`, NOT THE CURATED VIEW. The release row's only address is
 * an inferred Microsoft IP classified safe, so under the report's own filter state it is
 * not in `filteredResults` unless CaseDetail's re-injection put it there — and this
 * section must not depend on that. Same derivation as the Executive Brief's fact pack
 * (utils/quarantineRelease.js), so the two reports cannot disagree.
 *
 * 🔴 LIVE FLAGS, and `selectRelatedMessages` filters on them FIRST. The blob keeps related
 * rows for a flag the analyst later removed, and unlike the Brief this report has no
 * server reconcile pass to drop them. With no live flag there is no section at all.
 */
// The account holder's release of a FLAGGED message from quarantine. Rendered under
// "How It Started" (v1.203.0): it is part of how the attacker got in, and under "Other Accounts
// to Review" it read as a finding about other accounts (IR-20260902-tthomason-001). The Brief
// states it beside its entry point too.
function buildQuarantineReleaseParagraphs(results, phishingFlags, ctx, selectedTz) {
  const flags = Array.isArray(phishingFlags) ? phishingFlags : [];
  if (flags.length === 0) return '';
  const flaggedIndex = flaggedPhishMessageIndex(flags, results?.MessageTraceEvents || []);
  const releaseEvidence = collectReleaseEvidence(results?.GeneralEvents || []);
  // The releases of FLAGGED messages only — the case's other quarantine releases are not
  // this finding, and on the reference case there are two of them.
  const releasedFlagged = [];
  for (const [silverId, info] of flaggedIndex.entries()) {
    const ev = releaseEvidence.get(silverId);
    if (ev) releasedFlagged.push({ ...info, ...ev });
  }
  releasedFlagged.sort((a, b) => String(a.timeUtc || '').localeCompare(String(b.timeUtc || '')));
  let html = '';
  for (const rel of releasedFlagged) {
    const when = formatTsPlain(rel.timeUtc, selectedTz);
    const subject = maskSubject(rel.subject, !ctx.maskSubjects);
    let p = `<p><strong>The quarantined message was released.</strong> Microsoft had placed the`
      + ` message from ${esc(rel.sender || 'the flagged sender')}`;
    if (subject) p += ` (&ldquo;${esc(subject)}&rdquo;)`;
    p += ` in quarantine. It was released from quarantine by the account holder on ${esc(when)}.`;
    if (rel.reportedFalsePositive) {
      p += ' It was also reported to Microsoft as a false positive, which tells Microsoft'
        + ' that this sender is safe to deliver in future.';
    }
    p += '</p>';
    html += p;
  }
  return html;
}

function buildPhishingCampaignSection(results, phishingFlags, attackerWindow, ctx, selectedTz, caseData, urlClassifications = {}) {
  const flags = Array.isArray(phishingFlags) ? phishingFlags : [];
  const rawGeneral = results?.GeneralEvents || [];
  const rawTrace = results?.MessageTraceEvents || [];

  const flaggedIndex = flaggedPhishMessageIndex(flags, rawTrace);
  const releaseEvidence = collectReleaseEvidence(rawGeneral);

  const bounds = maliciousActivityBounds(attackerWindow);
  const selection = selectRelatedMessages(relatedPhishingRows(results), flags, bounds);
  const related = selection.rows;

  // No live flag => no section (the early return the review asked for). The account holder's
  // own release is stated under "How It Started" (buildQuarantineReleaseParagraphs), so with no
  // related row there is nothing for this section to say.
  if (flags.length === 0) return '';
  if (related.length === 0) return '';

  const RELATED_CAP = 25;
  const shown = related.slice(0, RELATED_CAP);
  const uniqueAccounts = new Set(
    related.map(r => String(r?.RecipientAddress || '').trim().toLowerCase()).filter(Boolean)
  ).size;

  // 🔴 THE CLAUSE FOLLOWS THE SELECTION'S OWN SCOPE MODE. `case_range` means no malicious
  // IP was classified, so there is no window to measure five days from — and printing the
  // ±5-day wording over that set would be a false statement about the rows on the page
  // (round-2 review finding 3).
  let scopeClause = 'during the reviewed period';
  if (selection.scope === RELATED_SCOPE_PAD) {
    scopeClause = 'within 5 days of the attacker activity recorded in this case';
  } else {
    const from = String(caseData?.startDate || '').substring(0, 10);
    const to = String(caseData?.endDate || '').substring(0, 10);
    if (from && to) scopeClause = `during the reviewed period ${from} to ${to}`;
  }

  let html = '';
  html += '<section>';
  html += '<h2>Other Accounts to Review — Mail From the Flagged Sender</h2>';

  // 🔴 "THE SAME MESSAGE" ONLY ON AN EXACT ID MATCH (NetworkMessageId / InternetMessageId with
  // the flagged email — relatedIsSameMessage), and another recipient's own click in that
  // message is named. Same rule as the Executive Brief, which the server re-derives.
  const sameRecipients = new Set();
  const clickedRecipients = new Set();
  const confirmedUrls = new Set(
    (results?.UrlClickEvents || [])
      .filter((e) => urlClassifications?.[e?.EventId] === 'confirmed')
      .map((e) => String(e?.Url || '').trim().toLowerCase())
      .filter(Boolean),
  );
  let clickedConfirmed = false;
  for (const r of related) {
    const who = String(r?.RecipientAddress || '').trim().toLowerCase();
    if (!who) continue;
    if (relatedIsSameMessage(r, flaggedIndex)) sameRecipients.add(who);
    const rc = relatedRecipientClicks(r);
    if (rc.length > 0) {
      clickedRecipients.add(who);
      if (rc.some((c) => confirmedUrls.has(String(c?.Url || '').trim().toLowerCase()))) clickedConfirmed = true;
    }
  }
  const sameAll = sameRecipients.size > 0 && sameRecipients.size >= uniqueAccounts;
  const sameSome = sameRecipients.size > 0 && !sameAll;
  const anyClick = clickedRecipients.size > 0;

  if (shown.length > 0) {
    const accountWord = uniqueAccounts === 1 ? 'account' : 'accounts';
    if (sameAll) {
      html += `<p>The same phishing email was also delivered to <strong>${uniqueAccounts} other`
        + ` ${accountWord}</strong> in this tenant ${esc(scopeClause)} — the same message as the account`
        + ' holder\'s, by its Microsoft message id. These accounts should be reviewed.'
        + ' <strong>This does not establish that they were compromised.</strong></p>';
    } else if (sameSome) {
      html += `<p>The same sender also delivered mail to <strong>${uniqueAccounts} other`
        + ` ${accountWord}</strong> in this tenant ${esc(scopeClause)}; ${sameRecipients.size} of them`
        + ' received the same message as the account holder, by its Microsoft message id. These'
        + ' accounts should be reviewed. <strong>This does not establish that they were compromised.</strong></p>';
    } else {
      html += `<p>The same sender also delivered mail to <strong>${uniqueAccounts} other`
        + ` ${accountWord}</strong> in this tenant ${esc(scopeClause)}. These accounts should be`
        + ' reviewed. <strong>This does not establish that they were compromised</strong>, and the'
        + ' records do not establish that they received the same message — only that the mail came'
        + ' from the sender identified as the phishing origin in this case.</p>';
    }
    if (anyClick) {
      const names = [...clickedRecipients];
      html += `<p><strong>${esc(names.join(', '))} clicked ${clickedConfirmed ? 'the malicious link' : 'a link'}`
        + ' in that mail.</strong> Review this account first.</p>';
    }
    html += `<p class="section-count">${
      related.length > shown.length
        ? `Showing ${shown.length} of ${related.length} messages, earliest first`
        : `${related.length} message${related.length === 1 ? '' : 's'}`
    }</p>`;
    html += '<table class="data-table">';
    html += '<thead><tr><th>Recipient</th><th>Received</th><th>Delivery Status</th>'
      + (sameSome ? '<th>Same Message</th>' : '')
      + (anyClick ? '<th>Link Clicked</th>' : '')
      + '<th>Released From Quarantine</th></tr></thead><tbody>';
    for (const r of shown) {
      const silverId = relatedMessageSilverId(r);
      // Presence-only "Yes": the operator's ask is WHETHER, not when — and the row already
      // carries its own received time, which is the investigator's "when did this account
      // get it".
      const rel = silverId && releaseEvidence.has(silverId) ? 'Yes' : '—';
      html += '<tr>'
        + `<td>${esc(r?.RecipientAddress || '')}</td>`
        + `<td>${esc(formatTs(r?.EventTimeUTC, selectedTz))}</td>`
        + `<td>${esc(r?.Status || '')}</td>`
        + (sameSome ? `<td>${relatedIsSameMessage(r, flaggedIndex) ? 'Yes' : '—'}</td>` : '')
        + (anyClick ? `<td>${(() => {
          const rc = relatedRecipientClicks(r);
          if (rc.length === 0) return '—';
          const t = rc[0]?.EventTimeUTC ? esc(formatTs(rc[0].EventTimeUTC, selectedTz)) : '';
          return `Yes${t ? ` — ${t}` : ''}${rc.length > 1 ? ` (${rc.length} clicks)` : ''}`;
        })()}</td>` : '')
        + `<td>${rel}</td>`
        + '</tr>';
    }
    html += '</tbody></table>';
  }

  html += '</section>';
  return html;
}

// ── Section: Investigation Timeline (All Events) ─────────────────────────────

/**
 * The Investigation Timeline's count line.
 *
 * It used to read "N events", which stopped being true the moment rows became access
 * periods and directory actions: the reference case renders 45 rows standing for
 * hundreds of source events. A reader cannot tell a short report from a filtered one
 * unless the report says which it is, so this reconciles the two numbers and names the
 * grouping that accounts for the difference.
 */
function timelineRowCount(rows, hasAppendix, omitted = []) {
  const list = Array.isArray(rows) ? rows : [];
  const total = underlyingEventTotal(list);
  const grouped = list.filter(e => underlyingEventCount(e) > 1);
  const off = Array.isArray(omitted) ? omitted : [];
  // The omission is stated with its own numbers, in the same sentence as the totals. A
  // client report that quietly showed fewer rows than the analyst's view would be the
  // exact drift this release removed.
  //
  // 🔴 The wording claims only what is KNOWN. The first draft said these rows "are the
  // account's own devices re-authenticating" — true of most of them, but 49 of the 112 on
  // the reference case sit on addresses the analyst had not classified at all, and
  // absence of attribution is not evidence of innocence. "Not attributed to the attacker"
  // is the fact; anything stronger is an interpretation the report would be putting in
  // the analyst's mouth.
  const held = off.length
    ? ` A further ${off.length} token-refresh period${off.length === 1 ? '' : 's'} `
      + `(${underlyingEventTotal(off)} authentications from `
      + `${new Set(off.map(e => e.IPAddress).filter(Boolean)).size} addresses) `
      + `${off.length === 1 ? 'is' : 'are'} not attributed to the attacker and `
      + `${off.length === 1 ? 'is' : 'are'} omitted from this table; `
      + `${hasAppendix ? 'each is listed in the Non-Interactive Sign-Ins appendix below.'
                       : 'they are listed in the Technical report.'}`
    : '';
  if (grouped.length === 0) {
    return `${list.length} event${list.length === 1 ? '' : 's'}.${held}`;
  }
  const folded = underlyingEventTotal(grouped);
  // 🔴 The pointer to the appendix is CONDITIONAL. This sentence is printed whether or not
  // the analyst asked for the per-log-type appendix, so an unconditional "every grouped
  // event is listed there" is a false statement on most reports -- and the one claim a
  // sceptical reader would check first.
  const where = hasAppendix
    ? ' Every grouped event is listed individually in the per-log-type appendix at the end of this report.'
    : ' The individual events are in the case record; add the per-log-type appendix to include them here.';
  return `${list.length} rows, covering ${total} recorded event${total === 1 ? '' : 's'} — `
    + `${grouped.length} of these rows group ${folded} events that Microsoft recorded separately `
    + `(continuous periods of access, and single administrative actions written as several entries).`
    + held + where;
}

/**
 * The Investigation Timeline.
 *
 * `nonInteractiveScope` is the ONE place the Client and Technical reports deliberately
 * differ, and the reason is the reader, not the data.
 *
 * The non-interactive feed is the victim's own devices almost all of the time: on the
 * reference case it is 123 rows of which **11** are attacker-attributed and the largest
 * single row is the user's laptop refreshing its token 7,394 times over 23 days. That is
 * essential to an analyst grading an address and useless to a client executive reading
 * "what did the attacker do" — the operator's words: *"that is not want we want to give
 * the executive since those have such limited value in understanding the impact"*.
 *
 * 🔴 It is a PRESENTATION scope, not a second definition of the view. Nothing is dropped
 * from the case or from the reconciliation: the count line still reports the full covered
 * total, the omission is stated with its own count and addresses, and every omitted row
 * is in the per-log-type appendix and in the Technical report. The whole point of v1.156.0
 * is that the report equals the screen, so an undisclosed report-only filter would undo
 * it — which is why this discloses rather than filters quietly.
 */
function buildFriendlyEvents(combinedEvents, ctx, selectedTz, includeLogBreakdown = false, opts = {}) {
  if (combinedEvents.length === 0) return '';
  const { nonInteractiveScope = 'all', attackerCtx = null } = opts;

  let shown = combinedEvents;
  let omitted = [];
  if (nonInteractiveScope === 'attacker') {
    const keep = (evt) => evt._eventType !== 'NonInteractiveSignIn'
      || !!attackerBasis(evt, attackerCtx);
    shown = combinedEvents.filter(keep);
    omitted = combinedEvents.filter(e => !keep(e));
  }

  const rows = shown.map(evt => {
    const vpnFlags = evt._vpnProxyFlags;
    // The same label function All Events uses (utils/allEventsRollups.js), so the report
    // cell and the screen cell cannot say different things.
    const app = rowAppLabel(evt);
    return `
    <tr>
      <td class="nowrap">${esc(formatTs(evt.EventTimeUTC, selectedTz))}</td>
      <td>${esc(evt._description)}</td>
      <td>${typeBadge(evt._eventType)}</td>
      <td>${esc(app)}</td>
      <td class="font-mono">${renderIPCell(evt, ctx)}</td>
      <td>${renderVPNProxy(vpnFlags)}</td>
      <td>${esc(evt._country)}</td>
    </tr>`;
  }).join('');

  return `
    <section>
      <h2>Investigation Timeline</h2>
      <p class="section-count">${timelineRowCount(shown, includeLogBreakdown, omitted)}</p>
      <table class="data-table">
        <thead><tr>
          <th>Timestamp</th><th>Description</th><th>Type</th><th>Application</th>
          <th>Client IP</th><th>VPN/Proxy</th><th>Country</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </section>`;
}

// ── Section: Mailbox Items Detail (per-item subjects) ────────────────────────
// Standalone table enumerating every individual mail item that was read,
// synced, deleted, or sent — surfacing the subject lines that the compact
// timeline descriptions ("read 11 mail items") omit. Subjects are masked
// unless the analyst has revealed them on the case (ctx.maskSubjects === false).

const MAIL_ACTION_LABELS = {
  MoveToDeletedItems: 'Moved to Deleted',
  SoftDelete: 'Soft-deleted',
  HardDelete: 'Permanently deleted',
};
const DELETE_ACTIONS = new Set(['Moved to Deleted', 'Soft-deleted', 'Permanently deleted']);

function buildMailboxItemsSection(combinedEvents, ctx, selectedTz) {
  const reveal = !ctx.maskSubjects;
  const rows = [];

  for (const evt of combinedEvents) {
    // Sent messages come from message trace, collapsed to one row per MESSAGE
    // (the feed itself is per-recipient) with every recipient named in `location`.
    if (evt._eventType === 'MessageTrace') {
      rows.push({
        ts: evt.EventTimeUTC,
        action: 'Sent',
        subject: evt.Subject || '',
        location: evt._collapsed ? recipientSummary(evt) : (evt.RecipientAddress || ''),
        messageId: evt.MessageId || evt.InternetMessageId || evt.NetworkMessageId || '',
        size: null,
        event: evt,
      });
      continue;
    }

    if (evt._eventType !== 'Exchange') continue;

    let action;
    if (evt.Operation === 'MailItemsAccessed') {
      const at = getMailAccessType(evt);
      action = at === 'Sync' ? 'Synced' : at === 'AppAccess' ? 'App-accessed' : at === 'Read' ? 'Read' : 'Accessed';
    } else {
      action = MAIL_ACTION_LABELS[evt.Operation];
    }
    if (!action) continue;

    const parsed = parseFolders(evt.Folders);
    if (!parsed) continue;
    const folders = Array.isArray(parsed) ? parsed : [parsed];
    for (const folder of folders) {
      const items = (folder && folder.FolderItems) || [];
      for (const item of items) {
        rows.push({
          ts: evt.EventTimeUTC,
          action,
          subject: item.Subject || '',
          location: folder.Path || '',
          messageId: item.InternetMessageId || '',
          size: item.SizeInBytes,
          event: evt,
        });
      }
    }
  }

  if (rows.length === 0) return '';

  rows.sort((a, b) => (a.ts || '') < (b.ts || '') ? -1 : (a.ts || '') > (b.ts || '') ? 1 : 0);

  const actionBadge = (a) => {
    const cls = a === 'Sent' ? 'badge-amber' : DELETE_ACTIONS.has(a) ? 'badge-red' : 'badge-blue';
    return `<span class="badge ${cls}">${esc(a)}</span>`;
  };
  const fmtSize = (b) => (b == null ? '' : `${(b / 1024).toFixed(1)} KB`);

  const showMsgId = rows.some(r => r.messageId);
  const showSize = rows.some(r => r.size != null);

  const bodyRows = rows.map(r => `
    <tr>
      <td class="nowrap">${esc(formatTs(r.ts, selectedTz))}</td>
      <td class="nowrap">${actionBadge(r.action)}</td>
      <td>${esc(r.subject ? maskSubject(r.subject, reveal) : '(no subject)')}</td>
      <td class="text-tiny">${esc(r.location || '—')}</td>
      ${showMsgId ? `<td class="font-mono text-tiny" style="word-break:break-all">${esc(r.messageId || '')}</td>` : ''}
      ${showSize ? `<td class="nowrap">${esc(fmtSize(r.size))}</td>` : ''}
      <td class="font-mono">${renderIPCell(r.event, ctx)}</td>
    </tr>`).join('');

  return `
    <section>
      <h2>Mailbox Items Detail</h2>
      <p class="section-count">${rows.length} mail item${rows.length !== 1 ? 's' : ''} read, deleted, or sent</p>
      <table class="data-table">
        <thead><tr>
          <th>Timestamp</th><th>Action</th><th>Subject</th><th>Folder / Recipient</th>
          ${showMsgId ? '<th>Message ID</th>' : ''}
          ${showSize ? '<th>Size</th>' : ''}
          <th>Client IP</th>
        </tr></thead>
        <tbody>${bodyRows}</tbody>
      </table>
    </section>`;
}

// ── Section: SharePoint Activity ─────────────────────────────────────────────

function buildSharePointSections(combinedEvents, ctx, selectedTz) {
  const spEvents = combinedEvents.filter(e => e._eventType === 'SharePoint' || e._eventType === 'SharePointList');
  if (spEvents.length === 0) return '';

  const groups = [
    { ops: ['FilePreviewed'], title: 'SharePoint Files Previewed' },
    { ops: ['FileAccessed'], title: 'SharePoint Files Accessed' },
    { ops: ['FileDownloaded'], title: 'SharePoint Files Downloaded' },
    { ops: ['FileModified', 'FileModifiedExtended'], title: 'SharePoint Files Modified' },
    { ops: ['FileUploaded'], title: 'SharePoint Files Uploaded' },
    { ops: ['FileDeleted', 'FileRecycled'], title: 'SharePoint Files Deleted' },
    { ops: ['FileMoved', 'FileRenamed'], title: 'SharePoint Files Moved/Renamed' },
    { ops: ['ListViewed', 'ListUpdated', 'ListColumnCreated', 'ListColumnUpdated'], title: 'SharePoint Lists' },
  ];

  let html = '';
  for (const group of groups) {
    const opsSet = new Set(group.ops);
    const events = spEvents.filter(e => opsSet.has(e.Operation));
    if (events.length === 0) continue;

    const rows = events.map(evt => `
      <tr>
        <td class="nowrap">${esc(formatTs(evt.EventTimeUTC, selectedTz))}</td>
        <td>${esc(evt._description)}</td>
        <td class="font-mono">${renderIPCell(evt, ctx)}</td>
        <td>${esc(evt._country)}</td>
      </tr>`).join('');

    html += `
      <section>
        <h3>${esc(group.title)}</h3>
        <p class="section-count">${events.length} events</p>
        <table class="data-table">
          <thead><tr><th>Timestamp</th><th>Description</th><th>Client IP</th><th>Country</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </section>`;
  }

  return html;
}

// ── Compact IP list (replaces MS-Infra proxy IPs, caps display) ─────────────

function compactIPList(ips, ctx, maxDisplay = 3) {
  if (!ips || ips.length === 0) return '<span class="text-muted">—</span>';

  const ipArr = Array.isArray(ips) ? ips : [ips];
  let hasMsInfra = false;
  const realIPs = [];

  for (const ip of ipArr) {
    const info = ctx.ipLookup && ctx.ipLookup[ip];
    const tier = info?.AzureServiceTier || info?.azureServiceTier || '';
    if (tier === 'MS-Backend' || tier === 'MS-Shared-Infra') {
      hasMsInfra = true;
    } else {
      realIPs.push(ip);
    }
  }

  // Deduplicate real IPs
  const unique = [...new Set(realIPs)];
  const parts = [];

  // Show up to maxDisplay real IPs with color coding
  const visible = unique.slice(0, maxDisplay);
  for (const ip of visible) {
    const cls = classifyIP(ip, ctx.safeIPSet, ctx.ipLookup, ctx.ipClassifications);
    parts.push(`<span class="${ipColorClass(cls)} font-mono">${esc(ip)}</span>`);
  }
  if (unique.length > maxDisplay) {
    parts.push(`<span class="text-muted">+${unique.length - maxDisplay} more</span>`);
  }

  // Add Microsoft Service label if any infra IPs were present
  if (hasMsInfra) {
    parts.push('<span class="ip-ms-infra">Microsoft Service</span>');
  }

  if (parts.length === 0) return '<span class="ip-ms-infra">Microsoft Service</span>';
  return parts.join(', ');
}

// ── Section: Enterprise Apps (Detailed) ──────────────────────────────────────

function buildEnterpriseAppsDetailed(apps, knownApps, ctx) {
  if (!apps || apps.length === 0) return '';

  const knownSet = new Set();
  if (knownApps) knownApps.forEach(ka => knownSet.add(ka.appId));

  const rows = apps.map(app => {
    const isKnown = knownSet.has(app.appId) || app.isKnown;
    const ipsHtml = compactIPList(app.ipsUsed, ctx);
    return `
    <tr>
      <td>${esc(app.displayName || 'Unknown')}</td>
      <td class="font-mono text-tiny">${esc(app.appId || '')}</td>
      <td class="text-center">${app.signInEvents || 0}</td>
      <td class="text-center">${app.exchangeEvents || 0}</td>
      <td class="text-center">${app.totalEvents || 0}</td>
      <td class="text-tiny">${ipsHtml}</td>
      <td>${isKnown ? '<span class="badge badge-green">Known</span>' : ''}</td>
      <td>${esc(app.category || '')}</td>
    </tr>`;
  }).join('');

  return `
    <section>
      <h2>Enterprise Applications</h2>
      <table class="data-table">
        <thead><tr><th>App Name</th><th>App ID</th><th>Sign-Ins</th><th>Exchange</th><th>Total</th><th>IPs Used</th><th>Known</th><th>Category</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </section>`;
}

// ── Section: IP Address Table ────────────────────────────────────────────────

/**
 * The IP Address List.
 *
 * Non-interactive-only addresses are merged in (see nonInteractiveRollup.js). Before
 * this, the reports rendered only `ipAddresses`, which is built from the interactive
 * feeds -- so on the reference case 37 of 59 token-refresh addresses were absent,
 * INCLUDING 3 the analyst had classified malicious. A report that names an attacker's
 * ASN while omitting two of its addresses is worse than one that says nothing.
 *
 * Added rows carry no enrichment (there is no row for them in the IP-enrichment table),
 * so location is blank and risk is null rather than zero -- "never scored" must not
 * render as "scored clean". They are marked `token only` so a reader can tell a
 * token-refresh sighting from an interactive sign-in.
 */
function buildIPAddressTable(ipAddresses, ipClassifications, tenantSafeIPs, safeIPSet, ipComments = {}, nonInteractiveEvents = []) {
  const merged = mergeNonInteractiveIpList(ipAddresses, nonInteractiveEvents);
  if (merged.length === 0) return '';
  const withheld = nonInteractiveExcludedIpCount(ipAddresses, nonInteractiveEvents);
  const addedCount = merged.length - (ipAddresses?.length || 0);

  const tenantSafeSet = new Set(tenantSafeIPs || []);

  const rows = merged.map(ip => {
    let status = 'default';
    if (ipClassifications[ip.IPAddress] && ipClassifications[ip.IPAddress] !== 'default') {
      status = ipClassifications[ip.IPAddress];
    } else if (tenantSafeSet.has(ip.IPAddress) || (safeIPSet && safeIPSet.has(ip.IPAddress))) {
      status = 'safe';
    }

    const loc = [ip.City, ip.State, ip.Country].filter(Boolean).join(', ');
    const rowClass = status === 'malicious' ? 'row-malicious' : status === 'suspect' ? 'row-suspect' : status === 'safe' ? 'row-safe' : '';

    return `
    <tr class="${rowClass}">
      <td>${ipStatusBadge(status)}</td>
      <td class="font-mono ${ipColorClass(status)}">${esc(ip.IPAddress)}${ip._nonInteractiveOnly
        ? ' <span style="font-family:inherit;font-size:9px;color:#94a3b8;white-space:nowrap;" title="Seen only in non-interactive sign-ins (token refresh / SSO reuse). No interactive sign-in from this address, and no IP-reputation record exists for it.">token only</span>'
        : ''}</td>
      <td>${esc(ipComments[ip.IPAddress] || '')}</td>
      <td>${esc(loc)}</td>
      <td>${esc(ip.Provider || '')}</td>
      <td class="text-center">${riskBadge(ip.RiskLevel)}</td>
      <td class="text-center">${boolCell(ip.IsVPN)}</td>
      <td class="text-center">${boolCell(ip.IsProxy)}</td>
      <td class="text-center">${boolCell(ip.IsTOR)}</td>
      <td class="text-center">${boolCell(ip.IsCompromised)}</td>
      <td class="text-center">${boolCell(ip.IsAnonymous)}</td>
      <td class="text-center">${boolCell(ip.DRMMMatched)}</td>
      <td class="text-center">${ip.EventCount || ''}</td>
    </tr>`;
  }).join('');

  return `
    <section>
      <h2>IP Address List</h2>
      <table class="data-table">
        <thead><tr>
          <th>Status</th><th>IP Address</th><th>Comment</th><th>Location</th><th>ISP/Provider</th>
          <th>Risk</th><th>VPN</th><th>Proxy</th><th>TOR</th><th>Comp.</th><th>Anon.</th><th>DRMM</th><th>Events</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      ${buildIpListScopeNote(addedCount, withheld)}
    </section>`;
}

/**
 * States what the list includes and what it withheld. A shortened list must never look
 * like a complete one, so the withheld count is printed rather than left implicit.
 */
function buildIpListScopeNote(addedCount, withheld) {
  if (!addedCount && !withheld) return '';
  const parts = [];
  if (addedCount) {
    parts.push(`${addedCount} address${addedCount === 1 ? '' : 'es'} marked <em>token only</em> `
      + 'appeared solely in non-interactive sign-ins (token refresh, SSO or device re-authentication) '
      + 'with no interactive sign-in. No IP-reputation record exists for these, so location and risk are blank.');
  }
  if (withheld) {
    // The operator's rule, stated so a reader knows the list is filtered and why.
    parts.push(`${withheld} further address${withheld === 1 ? '' : 'es'} `
      + `${withheld === 1 ? 'was' : 'were'} seen only in <strong>failed</strong> authentication attempts `
      + `and ${withheld === 1 ? 'is' : 'are'} not listed; a rejected attempt is not treated as evidence `
      + 'that an address was used.');
  }
  return `<p style="font-size:10px;color:#64748b;margin-top:6px;">${parts.join(' ')}</p>`;
}

// ── Section: Log Type Breakdown ──────────────────────────────────────────────

function buildLogBreakdown(filteredResults, ctx, selectedTz) {
  const fr = filteredResults;
  let html = '<section class="page-break"><h2>Output by Log Type</h2>';

  // Sign-In Logs
  const signIns = fr.SignInEvents || [];
  if (signIns.length > 0) {
    const rows = signIns.map(evt => {
      const flags = getVPNProxyFlagsForBreakdown(evt, ctx.ipLookup);
      return `<tr>
        <td class="nowrap">${esc(formatTs(evt.EventTimeUTC, selectedTz))}</td>
        <td>${esc(evt.ApplicationName || '')}</td>
        <td class="font-mono">${renderIP(evt.IPAddress, ctx, evt)}</td>
        <td>${renderVPNProxy(flags)}</td>
        <td>${esc(evt.Location?.Country || evt.IP_Country || '')}</td>
        <td>${esc(evt.DeviceOS || '')} / ${esc(parseBrowser(evt.UserAgent))}</td>
        <td>${esc(evt.DeviceHostname || '')}</td>
        <td class="${evt.ResultStatus?.toLowerCase() === 'failed' ? 'text-red-light' : 'text-green-light'}">${esc(evt.ResultStatus || '')}</td>
        <td class="text-center">${riskBadge(evt.RiskScore ?? evt.Risk_Total)}</td>
      </tr>`;
    }).join('');
    html += `<h3>Sign-In Logs (${signIns.length})</h3>
      <table class="data-table"><thead><tr>
        <th>Timestamp</th><th>Application</th><th>IP Address</th><th>VPN/Proxy/TOR</th><th>Country</th><th>OS / Browser</th><th>Device</th><th>Status</th><th>Risk</th>
      </tr></thead><tbody>${rows}</tbody></table>`;
  }

  // Non-Interactive Sign-Ins (token refresh / SSO reuse / PRT replay).
  //
  // 🔴 Load-bearing, not decoration: the Investigation Timeline rolls this feed into
  // access periods and its count line tells the reader every grouped event is listed
  // here. Without this section that sentence was false for exactly the rows an analyst
  // is most likely to want to check. Printed at the feed's own (session, IP, hour)
  // grain, which is the finest grain that exists -- the feed arrives pre-aggregated.
  const nonInteractive = fr.NonInteractiveSignInEvents || [];
  if (nonInteractive.length > 0) {
    const rows = nonInteractive.map(evt => {
      const ok = Number(evt.SuccessCount) || 0;
      const bad = Number(evt.FailureCount) || 0;
      return `<tr>
        <td class="nowrap">${esc(formatTs(evt.EventTimeUTC, selectedTz))}</td>
        <td class="nowrap">${esc(evt.LastEventTimeUTC && evt.LastEventTimeUTC !== evt.EventTimeUTC ? formatTs(evt.LastEventTimeUTC, selectedTz) : '')}</td>
        <td>${esc(nonInteractiveAppLabel(evt))}</td>
        <td class="font-mono">${renderIP(evt.IPAddress, ctx, evt)}</td>
        <td>${esc(evt.DeviceName || '')}</td>
        <td class="text-center">${esc(String(Number(evt.EventCount) || 0))}</td>
        <td class="text-center ${bad && !ok ? 'text-red-light' : ok && !bad ? 'text-green-light' : ''}">${esc(`${ok} ok / ${bad} failed`)}</td>
        <td class="text-tiny">${esc(evt.ErrorCodes || '')}</td>
        <td class="text-tiny font-mono">${esc(evt.SessionId || '')}</td>
      </tr>`;
    }).join('');
    const auths = nonInteractive.reduce((n, e) => n + (Number(e.EventCount) || 0), 0);
    html += `<h3>Non-Interactive Sign-Ins (${nonInteractive.length} hourly records, ${auths} authentications)</h3>
      <p class="muted">Tokens redeemed without the user signing in again — token refresh, single sign-on re-use, or replay of a stolen token. Microsoft reports this feed already grouped by session, address and hour, so each row below is a group of authentications rather than a single one.</p>
      <table class="data-table"><thead><tr>
        <th>From</th><th>Until</th><th>Application(s)</th><th>IP Address</th><th>Device</th><th>Auths</th><th>Outcome</th><th>Error codes</th><th>Session</th>
      </tr></thead><tbody>${rows}</tbody></table>`;
  }

  // Directory Audit
  const dirAudit = fr.DirectoryAuditEvents || [];
  if (dirAudit.length > 0) {
    const rows = dirAudit.map(evt => `<tr>
      <td class="nowrap">${esc(formatTs(evt.EventTimeUTC, selectedTz))}</td>
      <td>${esc(getDisplayOperation({ ...evt, _detail: getDirAuditDetail(evt) }))}</td>
      <td>${esc(getDirAuditDetail(evt))}</td>
      <td class="font-mono">${renderIP(evt.IPAddress, ctx, evt)}</td>
      <td>${esc(evt.ResultStatus || '')}</td>
    </tr>`).join('');
    html += `<h3>Directory Audit (${dirAudit.length})</h3>
      <table class="data-table"><thead><tr><th>Timestamp</th><th>Operation</th><th>Detail</th><th>IP Address</th><th>Status</th></tr></thead>
      <tbody>${rows}</tbody></table>`;
  }

  // Message Trace
  // One row per MESSAGE, not per recipient -- but every recipient is printed, each with
  // its own delivery status, because 45.4% of multi-recipient messages have more than one
  // outcome and a single "delivered" would assert delivery that did not happen.
  const msgTrace = collapseMessageTrace(fr.MessageTraceEvents || []);
  if (msgTrace.length > 0) {
    const recipCell = (evt) => {
      if (!evt._collapsed) return esc(evt.RecipientAddress || '');
      const lines = evt._recipients.map(r => `${esc(r.address)}${r.status && evt._statusMixed ? ` <span class="muted">(${esc(r.status)})</span>` : ''}`);
      const partial = evt._recipientsPartial
        ? `<div class="muted">showing ${evt._recipientCount} of ${evt._declaredRecipientCount} recipients on the message</div>`
        : '';
      return `${lines.join('<br>')}${partial}`;
    };
    const rows = msgTrace.map(evt => `<tr>
      <td class="nowrap">${esc(formatTs(evt.EventTimeUTC, selectedTz))}</td>
      <td>${esc(evt.Direction || '')}</td>
      <td>${esc(evt.SenderAddress || '')}</td>
      <td>${recipCell(evt)}</td>
      <td>${esc(evt.Subject ? maskSubject(evt.Subject, !ctx.maskSubjects) : '')}</td>
      <td>${esc(evt._collapsed ? recipientStatusSummary(evt) : (evt.Status || ''))}</td>
      <td class="font-mono">${renderIP(evt.IPAddress, ctx, evt)}</td>
    </tr>`).join('');
    html += `<h3>Message Trace (${msgTrace.length} message${msgTrace.length === 1 ? '' : 's'})</h3>
      <table class="data-table"><thead><tr><th>Timestamp</th><th>Direction</th><th>Sender</th><th>Recipient(s)</th><th>Subject</th><th>Status</th><th>From IP</th></tr></thead>
      <tbody>${rows}</tbody></table>`;
  }

  // URL Clicks
  const urlClicks = fr.UrlClickEvents || [];
  if (urlClicks.length > 0) {
    const rows = urlClicks.map(evt => `<tr>
      <td class="nowrap">${esc(formatTs(evt.EventTimeUTC, selectedTz))}</td>
      <td>${esc(evt.ActionType || '')}</td>
      <td class="url-cell">${esc(evt.Url || '')}</td>
      <td class="font-mono">${renderIP(evt.IPAddress, ctx, evt)}</td>
      <td>${esc(evt.ThreatType || '')}</td>
      <td class="text-tiny">${esc(evt.NetworkMessageId || '')}</td>
    </tr>`).join('');
    html += `<h3>URL Clicks (${urlClicks.length})</h3>
      <table class="data-table"><thead><tr><th>Timestamp</th><th>Action</th><th>URL</th><th>Client IP</th><th>Threat</th><th>Message ID</th></tr></thead>
      <tbody>${rows}</tbody></table>`;
  }

  // SharePoint Files (aggregated)
  const allSP = [...(fr.SharePointEvents || []), ...(fr.ExchangeEvents || [])];
  const spFiles = allSP.filter(e => FILE_OPERATIONS.has(e.Operation));
  if (spFiles.length > 0) {
    const fileMap = {};
    spFiles.forEach(evt => {
      let folders;
      try { folders = typeof evt.Folders === 'string' ? JSON.parse(evt.Folders) : evt.Folders; } catch { folders = null; }
      const fileName = folders?.FileName || evt.Operation;
      const sitePath = folders?.Path || '';
      const key = `${sitePath}|${fileName}`;
      if (!fileMap[key]) {
        fileMap[key] = { fileName, sitePath, ops: new Set(), count: 0, ips: new Set(), first: evt.EventTimeUTC, last: evt.EventTimeUTC };
      }
      const entry = fileMap[key];
      entry.ops.add(evt.Operation);
      entry.count++;
      if (evt.IPAddress) entry.ips.add(evt.IPAddress);
      if (evt.EventTimeUTC < entry.first) entry.first = evt.EventTimeUTC;
      if (evt.EventTimeUTC > entry.last) entry.last = evt.EventTimeUTC;
    });
    const fileRows = Object.values(fileMap).sort((a, b) => a.first < b.first ? -1 : 1).map(f => `<tr>
      <td>${esc(maskFileName(f.fileName, !ctx.maskSubjects))}</td>
      <td>${esc(f.sitePath)}</td>
      <td>${[...f.ops].map(o => esc(o)).join(', ')}</td>
      <td class="text-center">${f.count}</td>
      <td class="font-mono text-tiny">${[...f.ips].map(i => esc(i)).join(', ')}</td>
      <td class="nowrap">${esc(formatTs(f.first, selectedTz))}</td>
      <td class="nowrap">${esc(formatTs(f.last, selectedTz))}</td>
    </tr>`).join('');
    html += `<h3>SharePoint Files (${Object.keys(fileMap).length} unique files)</h3>
      <table class="data-table"><thead><tr><th>File Name</th><th>Site Path</th><th>Operations</th><th>Count</th><th>IPs</th><th>First</th><th>Last</th></tr></thead>
      <tbody>${fileRows}</tbody></table>`;
  }

  // SharePoint Lists (aggregated)
  const spLists = allSP.filter(e => LIST_OPERATIONS.has(e.Operation));
  if (spLists.length > 0) {
    const listMap = {};
    spLists.forEach(evt => {
      let folders;
      try { folders = typeof evt.Folders === 'string' ? JSON.parse(evt.Folders) : evt.Folders; } catch { folders = null; }
      const path = folders?.Path || '';
      const listName = path.split('/').pop() || path || 'unknown';
      const key = path || listName;
      if (!listMap[key]) {
        listMap[key] = { listName, path, ops: new Set(), count: 0, ips: new Set(), first: evt.EventTimeUTC, last: evt.EventTimeUTC };
      }
      const entry = listMap[key];
      entry.ops.add(evt.Operation);
      entry.count++;
      if (evt.IPAddress) entry.ips.add(evt.IPAddress);
      if (evt.EventTimeUTC < entry.first) entry.first = evt.EventTimeUTC;
      if (evt.EventTimeUTC > entry.last) entry.last = evt.EventTimeUTC;
    });
    const listRows = Object.values(listMap).sort((a, b) => a.first < b.first ? -1 : 1).map(l => `<tr>
      <td>${esc(l.listName)}</td>
      <td>${esc(l.path)}</td>
      <td>${[...l.ops].map(o => esc(o)).join(', ')}</td>
      <td class="text-center">${l.count}</td>
      <td class="font-mono text-tiny">${[...l.ips].map(i => esc(i)).join(', ')}</td>
      <td class="nowrap">${esc(formatTs(l.first, selectedTz))}</td>
      <td class="nowrap">${esc(formatTs(l.last, selectedTz))}</td>
    </tr>`).join('');
    html += `<h3>SharePoint Lists (${Object.keys(listMap).length} unique lists)</h3>
      <table class="data-table"><thead><tr><th>List Name</th><th>Path</th><th>Operations</th><th>Count</th><th>IPs</th><th>First</th><th>Last</th></tr></thead>
      <tbody>${listRows}</tbody></table>`;
  }

  // DLP
  const dlp = fr.DlpEvents || [];
  if (dlp.length > 0) {
    const rows = dlp.map(evt => `<tr>
      <td class="nowrap">${esc(formatTs(evt.EventTimeUTC, selectedTz))}</td>
      <td>${esc(evt.Operation || '')}</td>
      <td>${esc(evt.PolicyName || '')}</td>
      <td>${esc(evt.PolicyAction || '')}</td>
      <td>${esc(evt.SensitiveInfoTypes || '')}</td>
      <td class="font-mono">${renderIP(evt.IPAddress, ctx, evt)}</td>
      <td>${esc(evt.Workload || '')}</td>
    </tr>`).join('');
    html += `<h3>DLP (${dlp.length})</h3>
      <table class="data-table"><thead><tr><th>Timestamp</th><th>Operation</th><th>Policy</th><th>Action</th><th>Sensitive Info</th><th>IP</th><th>Workload</th></tr></thead>
      <tbody>${rows}</tbody></table>`;
  }

  // General Audit
  const general = fr.GeneralEvents || [];
  if (general.length > 0) {
    const rows = general.map(evt => `<tr>
      <td class="nowrap">${esc(formatTs(evt.EventTimeUTC, selectedTz))}</td>
      <td>${esc(evt.Operation || '')}</td>
      <td>${esc(evt.Workload || '')}</td>
      <td>${esc(evt.RecordType || '')}</td>
      <td>${esc(evt.ResultStatus || '')}</td>
      <td class="font-mono">${renderIP(evt.IPAddress, ctx, evt)}</td>
    </tr>`).join('');
    html += `<h3>General Audit (${general.length})</h3>
      <table class="data-table"><thead><tr><th>Timestamp</th><th>Operation</th><th>Workload</th><th>Record Type</th><th>Status</th><th>IP</th></tr></thead>
      <tbody>${rows}</tbody></table>`;
  }

  // Azure Activity
  const azure = fr.AzureActivityEvents || [];
  if (azure.length > 0) {
    const flatVal = v => (typeof v === 'object' && v) ? (v.localizedValue || v.value || '') : (v || '');
    const rows = azure.map(evt => `<tr>
      <td class="nowrap">${esc(formatTs(evt.EventTimeUTC, selectedTz))}</td>
      <td>${esc(flatVal(evt.Operation))}</td>
      <td>${esc(flatVal(evt.ResourceType))}</td>
      <td>${esc(evt.ResultStatus || '')}</td>
      <td class="font-mono">${renderIP(evt.IPAddress, ctx, evt)}</td>
      <td>${esc(flatVal(evt.Category))}</td>
    </tr>`).join('');
    html += `<h3>Azure Activity (${azure.length})</h3>
      <table class="data-table"><thead><tr><th>Timestamp</th><th>Operation</th><th>Resource Type</th><th>Status</th><th>IP</th><th>Category</th></tr></thead>
      <tbody>${rows}</tbody></table>`;
  }

  // Security Alerts (deduplicated)
  const alerts = deduplicateAlerts((fr.SecurityAlertEvents || []).filter(e => e.IsUserRelated));
  if (alerts.length > 0) {
    const sevBadge = s => {
      const c = s === 'high' ? 'badge-red' : s === 'medium' ? 'badge-amber' : s === 'low' ? 'badge-green' : 'badge-gray';
      return `<span class="badge ${c}">${esc(s || 'unknown')}</span>`;
    };
    const rows = alerts.map(evt => {
      const evidence = getSecurityAlertSummary(evt);
      const activityTime = evt.FirstActivityDateTime || evt.firstActivityDateTime || '';
      const mitre = evt.MitreTechniques || (evt.RawLog?.mitreTechniques || []).join(', ');
      return `<tr>
      <td class="nowrap">${esc(formatTs(evt.EventTimeUTC, selectedTz))}</td>
      <td class="nowrap">${activityTime ? esc(formatTs(activityTime, selectedTz)) : '\u2014'}</td>
      <td>${esc(evt.Title || '')}</td>
      <td class="text-center">${sevBadge(evt.Severity)}</td>
      <td style="max-width:300px;word-break:break-all;font-size:10px">${evidence ? esc(evidence) : '\u2014'}</td>
      <td>${esc(mitre || '')}</td>
    </tr>`;
    }).join('');
    html += `<h3>MSFT Security Alerts (${alerts.length})</h3>
      <table class="data-table"><thead><tr><th>Alert Time</th><th>Activity Time</th><th>Title</th><th>Severity</th><th>Evidence</th><th>MITRE</th></tr></thead>
      <tbody>${rows}</tbody></table>`;
  }

  // Security Incidents
  const incidents = (fr.SecurityIncidentEvents || []).filter(e => e.IsUserRelated);
  if (incidents.length > 0) {
    const sevBadge = s => {
      const c = s === 'high' ? 'badge-red' : s === 'medium' ? 'badge-amber' : s === 'low' ? 'badge-green' : 'badge-gray';
      return `<span class="badge ${c}">${esc(s || 'unknown')}</span>`;
    };
    const rows = incidents.map(evt => `<tr>
      <td class="nowrap">${esc(formatTs(evt.EventTimeUTC, selectedTz))}</td>
      <td>${esc(evt.DisplayName || '')}</td>
      <td class="text-center">${sevBadge(evt.Severity)}</td>
      <td>${esc(evt.Status || '')}</td>
      <td>${esc(evt.Classification || '')}</td>
      <td>${esc(evt.Determination || '')}</td>
      <td class="text-center">${evt.AlertCount || ''}</td>
    </tr>`).join('');
    html += `<h3>MSFT Security Incidents (${incidents.length})</h3>
      <table class="data-table"><thead><tr><th>Timestamp</th><th>Name</th><th>Severity</th><th>Status</th><th>Classification</th><th>Determination</th><th>Alerts</th></tr></thead>
      <tbody>${rows}</tbody></table>`;
  }

  html += '</section>';
  return html;
}

function getVPNProxyFlagsForBreakdown(event, ipLookup) {
  return {
    isVPN: event.IsVPN || event.RiskFactors?.IsVPN || false,
    isProxy: event.IsProxy || event.RiskFactors?.IsProxy || false,
    isTOR: event.IsTOR || event.RiskFactors?.IsTOR || false,
    isCompromised: event.IsCompromised || event.RiskFactors?.IsCompromised || false,
    isAnonymous: event.IsAnonymous || event.RiskFactors?.IsAnonymous || false,
  };
}

// ── CSS ──────────────────────────────────────────────────────────────────────

const REPORT_CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: #111827; color: #e5e7eb; margin: 0; padding: 24px 32px;
    font-size: 13px; line-height: 1.5;
  }
  section { margin-bottom: 28px; }
  h1 { color: #f3f4f6; font-size: 22px; margin: 0 0 12px; font-weight: 700; }
  h2 { color: #d1d5db; font-size: 16px; font-weight: 600; border-bottom: 1px solid #374151; padding-bottom: 6px; margin: 0 0 12px; }
  h3 { color: #9ca3af; font-size: 14px; font-weight: 600; margin: 20px 0 8px; }
  p { margin: 4px 0; }
  ul { margin: 0; padding-left: 20px; }

  .report-header { margin-bottom: 28px; border-bottom: 2px solid #374151; padding-bottom: 16px; }
  .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 24px; color: #9ca3af; font-size: 13px; }
  .meta-grid span { color: #d1d5db; font-weight: 500; }

  .compromise-box { background: rgba(127,29,29,0.15); border: 1px solid rgba(239,68,68,0.25); border-radius: 6px; padding: 12px 16px; }
  .compromise-box p { color: #e5e7eb; }
  .compromise-box strong { color: #fca5a5; }
  .remediation-box { background: rgba(6,78,59,0.15); border: 1px solid rgba(34,197,94,0.25); border-radius: 6px; padding: 10px 16px; }
  .remediation-box p { color: #d1fae5; }

  .findings-list { list-style: disc; padding-left: 24px; }
  .findings-list li { color: #e5e7eb; margin: 4px 0; font-size: 13px; }
  .app-list { list-style: disc; padding-left: 24px; columns: 2; }
  .app-list li { color: #d1d5db; margin: 2px 0; font-size: 13px; }

  .section-count { color: #6b7280; font-size: 12px; margin-bottom: 8px; }

  .data-table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .data-table th {
    background: #1f2937; color: #9ca3af; padding: 6px 10px; text-align: left;
    font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;
    border-bottom: 1px solid #374151; position: sticky; top: 0;
  }
  .data-table td { padding: 4px 10px; border-bottom: 1px solid #1f2937; vertical-align: top; }
  .data-table tr:nth-child(even) td { background: rgba(31,41,55,0.4); }

  .font-mono { font-family: 'Cascadia Code', 'Fira Code', 'Consolas', monospace; font-size: 11px; }
  .nowrap { white-space: nowrap; }
  .text-center { text-align: center; }
  .text-tiny { font-size: 10px; }
  .text-muted { color: #6b7280; }
  .url-cell { word-break: break-all; max-width: 400px; }

  /* IP status colors */
  .ip-malicious { color: #fca5a5; }
  .ip-suspect { color: #fcd34d; }
  .ip-safe { color: #86efac; }
  .ip-ms-infra { color: #93c5fd; }
  .ip-default { color: #d1d5db; }

  /* Row border markers */
  .row-malicious { border-left: 3px solid #ef4444; }
  .row-suspect { border-left: 3px solid #f59e0b; }
  .row-safe { border-left: 3px solid #22c55e; }

  /* Text colors */
  .text-red-light { color: #fca5a5; }
  .text-amber-light { color: #fcd34d; }
  .text-green-light { color: #86efac; }

  /* Badges */
  .badge { display: inline-block; padding: 1px 7px; border-radius: 9999px; font-size: 11px; font-weight: 500; border: 1px solid; }
  .badge-red { background: rgba(220,38,38,0.2); color: #fca5a5; border-color: rgba(220,38,38,0.3); }
  .badge-amber { background: rgba(217,119,6,0.2); color: #fcd34d; border-color: rgba(217,119,6,0.3); }
  .badge-green { background: rgba(22,163,74,0.2); color: #86efac; border-color: rgba(22,163,74,0.3); }
  .badge-blue { background: rgba(37,99,235,0.2); color: #93c5fd; border-color: rgba(37,99,235,0.3); }
  .badge-gray { background: rgba(75,85,99,0.2); color: #9ca3af; border-color: rgba(75,85,99,0.3); }

  /* Type badges */
  .type-signin { background: rgba(30,64,175,0.2); color: #93c5fd; border-color: rgba(30,64,175,0.3); }
  .type-exchange { background: rgba(88,28,135,0.2); color: #d8b4fe; border-color: rgba(88,28,135,0.3); }
  .type-sharepoint { background: rgba(6,95,70,0.2); color: #6ee7b7; border-color: rgba(6,95,70,0.3); }
  .type-splist { background: rgba(17,94,89,0.2); color: #5eead4; border-color: rgba(17,94,89,0.3); }
  .type-diraudit { background: rgba(49,46,129,0.2); color: #a5b4fc; border-color: rgba(49,46,129,0.3); }
  .type-msgtrace { background: rgba(146,64,14,0.2); color: #fcd34d; border-color: rgba(146,64,14,0.3); }
  .type-urlclick { background: rgba(159,18,57,0.2); color: #fda4af; border-color: rgba(159,18,57,0.3); }
  .type-riskdet { background: rgba(153,27,27,0.2); color: #fca5a5; border-color: rgba(153,27,27,0.3); }
  .type-tokenuse { background: rgba(22,78,99,0.25); color: #67e8f9; border-color: rgba(14,116,144,0.35); }
  .type-riskyuser { background: rgba(127,29,29,0.2); color: #fecaca; border-color: rgba(127,29,29,0.3); }
  .type-dlp { background: rgba(154,52,18,0.2); color: #fdba74; border-color: rgba(154,52,18,0.3); }
  .type-general { background: rgba(55,65,81,0.2); color: #d1d5db; border-color: rgba(55,65,81,0.3); }
  .type-azure { background: rgba(12,74,110,0.2); color: #7dd3fc; border-color: rgba(12,74,110,0.3); }
  .type-alert-evidence { background: rgba(153,27,27,0.4); color: #fca5a5; border-color: rgba(153,27,27,0.3); font-style: italic; }
  .type-alert { background: rgba(153,27,27,0.3); color: #fecaca; border-color: rgba(153,27,27,0.4); }
  .type-incident { background: rgba(159,18,57,0.3); color: #fecdd3; border-color: rgba(159,18,57,0.4); }
  .type-default { background: rgba(55,65,81,0.2); color: #9ca3af; border-color: rgba(55,65,81,0.3); }

  /* Print actions bar */
  .actions-bar { position: fixed; top: 0; left: 0; right: 0; background: #1f2937; padding: 10px 32px; z-index: 100; border-bottom: 1px solid #374151; display: flex; gap: 12px; align-items: center; }
  .actions-bar button { background: #1d4ed8; color: white; border: none; padding: 6px 18px; border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 500; }
  .actions-bar button:hover { background: #1e40af; }
  .actions-bar .report-label { color: #9ca3af; font-size: 12px; }
  body { padding-top: 60px; }

  /* Page break helpers */
  .page-break { page-break-before: always; }

  @media print {
    .actions-bar { display: none !important; }
    body { background: white; color: #1f2937; padding: 12px; padding-top: 12px; font-size: 11px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .report-header { border-color: #d1d5db; }
    h2 { border-color: #d1d5db; color: #374151; }
    h3 { color: #4b5563; }
    .meta-grid { color: #4b5563; }
    .meta-grid span { color: #1f2937; }
    .data-table th { background: #f3f4f6; color: #374151; border-color: #d1d5db; }
    .data-table td { border-color: #e5e7eb; }
    .data-table tr:nth-child(even) td { background: #f9fafb; }
    .compromise-box { background: #fef2f2; border-color: #fecaca; }
    .compromise-box p { color: #1f2937; }
    .compromise-box strong { color: #dc2626; }
    .remediation-box { background: #f0fdf4; border-color: #bbf7d0; }
    .remediation-box p { color: #1f2937; }
    .ip-malicious { color: #dc2626; }
    .ip-suspect { color: #d97706; }
    .ip-safe { color: #16a34a; }
    .ip-ms-infra { color: #2563eb; }
    .ip-default { color: #374151; }
    .text-red-light { color: #dc2626; }
    .text-amber-light { color: #d97706; }
    .text-green-light { color: #16a34a; }
    .text-muted { color: #6b7280; }
    .findings-list li, .app-list li { color: #1f2937; }
    .section-count { color: #6b7280; }
    table { page-break-inside: auto; }
    tr { page-break-inside: avoid; }
    h2, h3 { page-break-after: avoid; }
    section { page-break-before: auto; }
    .page-break { page-break-before: always; }
  }
`;

// ── Assemble full HTML document ──────────────────────────────────────────────

function buildFullHtml(title, actionsLabel, bodyHtml) {
  // NOTE: no inline onclick / inline <script> here — the app's CSP is
  // `script-src 'self'` (no 'unsafe-inline'), and the report tab inherits that
  // CSP, so any inline JS in this document is blocked. The action buttons carry
  // stable ids and are wired from the PARENT window in openReportInNewTab()
  // (parent JS is bundled/'self', so it's allowed).
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${esc(title)}</title>
  <style>${REPORT_CSS}</style>
</head>
<body>
  <div class="actions-bar">
    <button id="report-export-pdf" type="button">Export to PDF</button>
    <button id="report-export-html" type="button">Export to HTML</button>
    <span class="report-label">${esc(actionsLabel)}</span>
  </div>
  ${bodyHtml}
</body>
</html>`;
}

// Open the report in a new tab and wire its action buttons FROM THE PARENT.
// The report document inherits the app CSP (`script-src 'self'`), which blocks
// inline handlers/scripts — so we open a same-origin about:blank tab via
// document.write (synchronous: the DOM exists immediately) and attach the click
// handlers from here, where the code is bundled 'self' JS.
export function openReportInNewTab(html, filename) {
  const win = window.open('', '_blank');
  if (!win) {
    alert('Could not open the report — please allow pop-ups for this site and try again.');
    return;
  }
  win.document.write(html);
  win.document.close();

  try {
    const pdfBtn = win.document.getElementById('report-export-pdf');
    if (pdfBtn) {
      pdfBtn.addEventListener('click', () => { win.focus(); win.print(); });
    }
    const htmlBtn = win.document.getElementById('report-export-html');
    if (htmlBtn) {
      htmlBtn.addEventListener('click', () => {
        // Serialize a clean, standalone copy (strip the actions toolbar).
        const root = win.document.documentElement.cloneNode(true);
        const bar = root.querySelector('.actions-bar');
        if (bar) bar.remove();
        const out = '<!DOCTYPE html>\n' + root.outerHTML;
        const blob = new Blob([out], { type: 'text/html;charset=utf-8' });
        const url = win.URL.createObjectURL(blob);
        const a = win.document.createElement('a');
        a.href = url;
        a.download = filename || 'report.html';
        win.document.body.appendChild(a);
        a.click();
        a.remove();
        win.setTimeout(() => win.URL.revokeObjectURL(url), 2000);
      });
    }
  } catch (e) {
    // Wiring is best-effort; the report itself still renders for viewing.
    console.error('Failed to wire report export buttons:', e);
  }
}

// ── Prepare shared context ───────────────────────────────────────────────────

function prepareContext(params) {
  const {
    filteredResults, ipClassifications, effectiveSafeIPSet,
    tenantSafeIPs, knownApps, results, maskSubjects = false,
    ipComments = {},
  } = params;

  const ipLookup = buildIpLookup(filteredResults.filtered?.IPAddresses || filteredResults.IPAddresses || []);
  const safeIPSet = effectiveSafeIPSet || new Set();

  const knownAppIdSet = new Set();
  if (knownApps) knownApps.forEach(ka => knownAppIdSet.add(ka.appId));
  const apps = results?.enterpriseApps || [];
  apps.forEach(a => { if (a.isKnown || a.isKnownApp) knownAppIdSet.add(a.appId || a.AppId); });

  return { ipLookup, safeIPSet, ipClassifications, knownAppIdSet, tenantSafeIPs, knownApps, maskSubjects, ipComments };
}

// ── Public API ───────────────────────────────────────────────────────────────

export function openClientReport(params) {
  const {
    caseData, filteredResults, attackerWindow, ipClassifications,
    urlClassifications, selectedTz, includeLogBreakdown, results,
    hideMsSubstrate = false,
    hideOutlookNoise = false,
    // 🔴 THE LIVE FLAGS, passed explicitly. `ctx` carries neither `results` nor the flags,
    // and `caseData.phishingFlags` is loaded once and never re-set by the toggle handler,
    // so reading it here would render a section from a flag set the analyst has since
    // changed. CaseDetail passes its own state.
    phishingFlags = [],
    // v1.182.0 — the "credentials captured, no access found" attestation (admin-set).
    compromiseScope = 'access_confirmed',
  } = params;

  const ctx = prepareContext(params);
  const fr = filteredResults.filtered || filteredResults;
  const noAccess = compromiseScope === 'credentials_captured_no_access_found';

  // The SAME stream the analyst signed off on in All Events. `includeNonInteractive` +
  // `rollups` are what make it the same: without them this report carried 620 per-event
  // sign-in rows and 45 directory rows where the screen showed 6 periods and 13 actions,
  // and it carried NOTHING from the non-interactive feed -- the feed that holds token
  // replay, and 3 of the reference case's 9 malicious addresses.
  const combinedEvents = collapseMessageTraceInStream(combineAndTagEvents({
    filteredResults: fr,
    ipLookup: ctx.ipLookup,
    knownAppIdSet: ctx.knownAppIdSet,
    hideKnownApps: true,
    hideMsSubstrate,
    hideOutlookNoise,
    ipClassifications,
    targetUpn: caseData.userPrincipalName,
    maskSubjects: ctx.maskSubjects,
    includeNonInteractive: true,
    rollups: true,
  }));

  // 🔴 THE EXECUTIVE BRIEF'S OWN FACT PACK, built from the same inputs. The checklist and the
  // entry-point statement are read from it rather than re-derived, so the two documents a
  // client may hold about one case cannot answer the same question differently
  // (Issues/2026-10-26_Client-Report-Match-Executive-Brief-TODO.md). CaseDetail passes the
  // Brief's own params (analyst marks, session marks, dismissals) for that reason. A failure
  // here drops only those two sections -- it never brings back the unattributed counts they
  // replaced, which were the false statements this exists to remove.
  let pack = null;
  try {
    pack = buildExecReportFactPack({
      ...params,
      caseData: { ...caseData, phishingFlags, compromiseScope },
      tzIana: params.tzIana || getTzIana(selectedTz),
      tzLabel: params.tzLabel || selectedTz,
    });
  } catch (err) {
    console.error('[ClientReport] Could not build the attacker checklist:', err);
  }

  let body = '';

  // Part 1: Executive Brief
  body += buildHeaderSection(caseData, selectedTz, 'client');
  body += buildExecutiveSummary(attackerWindow, ctx.ipLookup, selectedTz, noAccess);
  body += buildEntryPointStatement(pack, results, selectedTz,
    buildQuarantineReleaseParagraphs(results, phishingFlags, ctx, selectedTz));
  // The phishing-kit sign-in that captured the credentials/token, framed as the
  // user's OWN activity (own IP, relayed through the kit) rather than attacker-IP
  // activity — placed before Attacker Origin because it is how it started.
  body += buildAiTMLandingSection(combinedEvents, ctx, selectedTz, attackerWindow);
  body += buildAttackerOrigin(attackerWindow, ctx.ipLookup, combinedEvents, ctx.ipComments, fr.NonInteractiveSignInEvents);
  body += buildKeyFindings(combinedEvents, attackerWindow, noAccess);
  body += buildAttackerChecklist(pack);
  body += buildConfirmedUrlClicks(results, urlClassifications, selectedTz);
  // The quarantine release + the accounts the flagged sender also reached. Client report
  // ONLY (the Technical report is untouched); see buildPhishingCampaignSection for why
  // every word of the lead framing is what it is.
  body += buildPhishingCampaignSection(results, phishingFlags, attackerWindow, ctx, selectedTz, caseData, urlClassifications);
  body += buildRemediation(attackerWindow, selectedTz);

  // Part 2: Detailed Findings
  body += '<div class="page-break"></div>';
  body += '<section><h2 style="font-size:18px; border:none; padding-bottom:0;">Detailed Findings</h2></section>';
  // Client report: only the attacker-attributed token-refresh rows appear in the timeline
  // table. The rest are the account's own devices and are stated as a count instead —
  // see buildFriendlyEvents. Attribution is built from the UNFILTERED results so a session
  // seen from a malicious address is still recognised when the pills hid part of it.
  body += buildFriendlyEvents(combinedEvents, ctx, selectedTz, includeLogBreakdown, {
    nonInteractiveScope: 'attacker',
    attackerCtx: buildAttackerContext(results, ipClassifications),
  });
  body += buildMailboxItemsSection(combinedEvents, ctx, selectedTz);
  body += buildSharePointSections(combinedEvents, ctx, selectedTz);
  body += buildEnterpriseAppsDetailed(results?.enterpriseApps, ctx.knownApps, ctx);
  body += buildIPAddressTable(fr.IPAddresses, ipClassifications, ctx.tenantSafeIPs, ctx.safeIPSet, ctx.ipComments, fr.NonInteractiveSignInEvents);
  body += buildEnterpriseAppsSimple(results?.enterpriseApps);

  if (includeLogBreakdown) {
    body += buildLogBreakdown(fr, ctx, selectedTz);
  }

  const title = `Client Report — ${caseData.userPrincipalName} — ${new Date().toLocaleDateString()}`;
  const filename = `${(caseData.caseId || 'report').replace(/[^A-Za-z0-9_-]+/g, '_')}-Client-Report.html`;
  const html = buildFullHtml(title, 'Client Report', body);
  openReportInNewTab(html, filename);
}

export function openTechnicalReport(params) {
  const {
    caseData, filteredResults, attackerWindow, ipClassifications,
    urlClassifications, selectedTz, includeLogBreakdown, results,
    hideMsSubstrate = false,
    hideOutlookNoise = false,
    // v1.182.0 — the "credentials captured, no access found" attestation (admin-set).
    compromiseScope = 'access_confirmed',
  } = params;

  const ctx = prepareContext(params);
  const fr = filteredResults.filtered || filteredResults;
  const noAccess = compromiseScope === 'credentials_captured_no_access_found';

  // The SAME stream the analyst signed off on in All Events. `includeNonInteractive` +
  // `rollups` are what make it the same: without them this report carried 620 per-event
  // sign-in rows and 45 directory rows where the screen showed 6 periods and 13 actions,
  // and it carried NOTHING from the non-interactive feed -- the feed that holds token
  // replay, and 3 of the reference case's 9 malicious addresses.
  const combinedEvents = collapseMessageTraceInStream(combineAndTagEvents({
    filteredResults: fr,
    ipLookup: ctx.ipLookup,
    knownAppIdSet: ctx.knownAppIdSet,
    hideKnownApps: true,
    hideMsSubstrate,
    hideOutlookNoise,
    ipClassifications,
    targetUpn: caseData.userPrincipalName,
    maskSubjects: ctx.maskSubjects,
    includeNonInteractive: true,
    rollups: true,
  }));

  let body = '';

  body += buildHeaderSection(caseData, selectedTz, 'technical');
  body += buildCompromiseSummary(attackerWindow, ctx.ipLookup, selectedTz, noAccess);
  // The phishing-kit sign-in (user's own IP, relayed through the AiTM kit) — the
  // initial-access / token-capture leg, stated explicitly so it is not read as
  // attacker-IP activity nor lost among the timeline rows.
  body += buildAiTMLandingSection(combinedEvents, ctx, selectedTz, attackerWindow);
  body += buildConfirmedUrlClicks(results, urlClassifications, selectedTz);
  body += buildFriendlyEvents(combinedEvents, ctx, selectedTz, includeLogBreakdown);
  body += buildMailboxItemsSection(combinedEvents, ctx, selectedTz);
  body += buildSharePointSections(combinedEvents, ctx, selectedTz);
  body += buildEnterpriseAppsDetailed(results?.enterpriseApps, ctx.knownApps, ctx);
  body += buildIPAddressTable(fr.IPAddresses, ipClassifications, ctx.tenantSafeIPs, ctx.safeIPSet, ctx.ipComments, fr.NonInteractiveSignInEvents);

  if (includeLogBreakdown) {
    body += buildLogBreakdown(fr, ctx, selectedTz);
  }

  const title = `Technical Report — ${caseData.userPrincipalName} — ${new Date().toLocaleDateString()}`;
  const filename = `${(caseData.caseId || 'report').replace(/[^A-Za-z0-9_-]+/g, '_')}-Technical-Report.html`;
  const html = buildFullHtml(title, 'Technical Report', body);
  openReportInNewTab(html, filename);
}
